import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useSearchParams, useParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { ShoppingBag, Search, Menu, X, Plus, Minus, Trash2, ChevronRight, Package, Truck, CheckCircle, User, LogOut, BarChart3, Zap, MapPin, ArrowRight } from "lucide-react";
import Marquee from "react-fast-marquee";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "./components/ui/sheet";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Badge } from "./components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Label } from "./components/ui/label";
import { Separator } from "./components/ui/separator";
import { ScrollArea } from "./components/ui/scroll-area";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getSessionId = () => {
  let sessionId = localStorage.getItem("vl_session");
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem("vl_session", sessionId);
  }
  return sessionId;
};

// ============= HEADER =============
const Header = ({ cart }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery)}`);
      setSearchOpen(false);
      setSearchQuery("");
    }
  };

  return (
    <header className="glass-header sticky top-0 z-50" data-testid="header">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-20">
          <button className="md:hidden p-2 text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn">
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          <Link to="/" className="flex items-center" data-testid="logo">
            <span className="font-heading text-xl md:text-2xl font-bold tracking-tight text-white">VORWAHL<span className="text-[#00FF9D]">LIEBE</span></span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            <Link to="/shop" className="font-body text-sm uppercase tracking-widest text-slate-400 hover:text-[#00FF9D] transition-colors" data-testid="nav-shop">Shop</Link>
            <Link to="/vorwahlen" className="font-body text-sm uppercase tracking-widest text-slate-400 hover:text-[#00FF9D] transition-colors" data-testid="nav-vorwahlen">Vorwahlen</Link>
            <Link to="/about" className="font-body text-sm uppercase tracking-widest text-slate-400 hover:text-[#00FF9D] transition-colors">Über uns</Link>
          </nav>

          <div className="flex items-center gap-4">
            <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
              <DialogTrigger asChild>
                <button className="p-2 text-slate-400 hover:text-[#00FF9D] transition-colors" data-testid="search-btn"><Search size={20} /></button>
              </DialogTrigger>
              <DialogContent className="sharp bg-[#0A0A0A] border border-white/10">
                <DialogHeader><DialogTitle className="font-heading text-2xl text-white">SUCHE</DialogTitle></DialogHeader>
                <form onSubmit={handleSearch} className="mt-4">
                  <Input placeholder="Vorwahl oder Stadt..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="sharp font-mono text-lg bg-[#050505] border-white/10 text-white" autoFocus data-testid="search-input" />
                  <Button type="submit" className="w-full mt-4 sharp btn-neon" data-testid="search-submit">Suchen</Button>
                </form>
              </DialogContent>
            </Dialog>

            <Link to="/admin" className="hidden md:block p-2 text-slate-400 hover:text-[#00FF9D] transition-colors" data-testid="admin-link"><User size={20} /></Link>
          </div>
        </div>

        {mobileMenuOpen && (
          <nav className="md:hidden py-6 border-t border-white/10 animate-fade-up">
            <div className="flex flex-col gap-4">
              <Link to="/shop" onClick={() => setMobileMenuOpen(false)} className="font-body text-sm uppercase tracking-widest text-slate-400 hover:text-[#00FF9D]">Shop</Link>
              <Link to="/vorwahlen" onClick={() => setMobileMenuOpen(false)} className="font-body text-sm uppercase tracking-widest text-slate-400 hover:text-[#00FF9D]">Vorwahlen</Link>
              <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="font-body text-sm uppercase tracking-widest text-slate-400 hover:text-[#00FF9D]">Über uns</Link>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

// ============= CART DRAWER =============
const CartDrawer = ({ cart }) => {
  const navigate = useNavigate();

  const updateQuantity = async (productId, variantId, newQuantity) => {
    try {
      await axios.put(`${API}/cart/${getSessionId()}/update`, null, { params: { product_id: productId, variant_id: variantId, quantity: newQuantity } });
      window.dispatchEvent(new Event("cartUpdated"));
    } catch (error) { toast.error("Fehler beim Aktualisieren"); }
  };

  const removeItem = async (productId, variantId) => {
    try {
      await axios.delete(`${API}/cart/${getSessionId()}/remove`, { params: { product_id: productId, variant_id: variantId } });
      window.dispatchEvent(new Event("cartUpdated"));
      toast.success("Artikel entfernt");
    } catch (error) { toast.error("Fehler beim Entfernen"); }
  };

  const subtotal = cart?.items?.reduce((sum, item) => sum + item.price * item.quantity, 0) || 0;
  const shipping = subtotal >= 50 ? 0 : 4.99;
  const total = subtotal + shipping;

  if (!cart?.items?.length) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center">
        <ShoppingBag size={48} className="text-gray-300 mb-4" />
        <p className="font-body text-gray-500">Dein Warenkorb ist leer</p>
        <Button onClick={() => navigate("/shop")} className="mt-4 sharp btn-primary" data-testid="shop-now-btn">Jetzt shoppen</Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <ScrollArea className="flex-1 -mx-6 px-6">
        <div className="space-y-4 py-4">
          {cart.items.map((item) => (
            <div key={`${item.product_id}-${item.variant_id}`} className="flex gap-4 border-b border-gray-100 pb-4">
              <div className="w-20 h-20 bg-gray-100 flex-shrink-0">
                <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-body font-semibold text-sm truncate">{item.name}</h4>
                <p className="font-mono text-xs text-gray-500 uppercase">{item.size} / {item.color}</p>
                <p className="font-mono text-sm mt-1">€{item.price.toFixed(2)}</p>
                <div className="flex items-center gap-2 mt-2">
                  <button onClick={() => updateQuantity(item.product_id, item.variant_id, item.quantity - 1)} className="w-6 h-6 flex items-center justify-center border border-black hover:bg-black hover:text-white transition-colors"><Minus size={12} /></button>
                  <span className="font-mono text-sm w-8 text-center">{item.quantity}</span>
                  <button onClick={() => updateQuantity(item.product_id, item.variant_id, item.quantity + 1)} className="w-6 h-6 flex items-center justify-center border border-black hover:bg-black hover:text-white transition-colors"><Plus size={12} /></button>
                  <button onClick={() => removeItem(item.product_id, item.variant_id)} className="ml-auto text-gray-400 hover:text-red-500 transition-colors"><Trash2 size={16} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>

      <div className="border-t border-black pt-4 mt-auto space-y-2">
        <div className="flex justify-between font-body text-sm"><span>Zwischensumme</span><span className="font-mono">€{subtotal.toFixed(2)}</span></div>
        <div className="flex justify-between font-body text-sm"><span>Versand</span><span className="font-mono">{shipping === 0 ? "KOSTENLOS" : `€${shipping.toFixed(2)}`}</span></div>
        {subtotal < 50 && <p className="text-xs text-gray-500">Noch €{(50 - subtotal).toFixed(2)} für kostenlosen Versand</p>}
        <Separator className="my-2" />
        <div className="flex justify-between font-body font-bold"><span>Gesamt</span><span className="font-mono text-lg">€{total.toFixed(2)}</span></div>
        <Button onClick={() => navigate("/checkout")} className="w-full mt-4 sharp btn-primary py-4" data-testid="checkout-btn">Zur Kasse</Button>
      </div>
    </div>
  );
};

// ============= FOOTER =============
const Footer = () => (
  <footer className="bg-[#0A0A0A] border-t border-white/5 py-20" data-testid="footer">
    <div className="max-w-4xl mx-auto px-6 md:px-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
        <div className="col-span-2 md:col-span-1">
          <Link to="/" className="block mb-4">
            <span className="font-heading text-lg font-medium text-white">VorwahlLiebe</span>
          </Link>
          <p className="font-body text-sm text-gray-600 leading-relaxed">Heimatliebe seit 2024.</p>
        </div>
        <div>
          <h4 className="font-body font-medium text-sm mb-4 text-white">Shop</h4>
          <ul className="space-y-3">
            <li><Link to="/shop" className="text-gray-500 hover:text-white transition-colors duration-300 text-sm">Alle Produkte</Link></li>
            <li><Link to="/vorwahlen" className="text-gray-500 hover:text-white transition-colors duration-300 text-sm">Vorwahlen</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-body font-medium text-sm mb-4 text-white">Info</h4>
          <ul className="space-y-3">
            <li><Link to="/about" className="text-gray-500 hover:text-white transition-colors duration-300 text-sm">Über uns</Link></li>
            <li><Link to="/widerruf" className="text-gray-500 hover:text-white transition-colors duration-300 text-sm">Widerruf</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-body font-medium text-sm mb-4 text-white">Legal</h4>
          <ul className="space-y-3">
            <li><Link to="/impressum" className="text-gray-500 hover:text-white transition-colors duration-300 text-sm">Impressum</Link></li>
            <li><Link to="/datenschutz" className="text-gray-500 hover:text-white transition-colors duration-300 text-sm">Datenschutz</Link></li>
            <li><Link to="/agb" className="text-gray-500 hover:text-white transition-colors duration-300 text-sm">AGB</Link></li>
          </ul>
        </div>
      </div>
      <Separator className="my-12 bg-white/5" />
      <div className="text-center">
        <p className="font-mono text-xs text-gray-600">© 2025 VorwahlLiebe — Interpohl Solutions GmbH i. Gr.</p>
      </div>
    </div>
  </footer>
);

// ============= HOME PAGE =============
const Home = ({ addToCart }) => {
  const navigate = useNavigate();
  const [vorwahlInput, setVorwahlInput] = useState("");

  const handleVorwahlSearch = (e) => {
    e.preventDefault();
    if (vorwahlInput.trim()) navigate(`/vorwahlen?search=${encodeURIComponent(vorwahlInput)}`);
  };

  return (
    <main data-testid="home-page" className="bg-[#050505]">
      {/* Hero Section - Cyberpunk Full Image */}
      <section 
        className="hero-cyber"
        style={{
          backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/1ed892fa-e1a3-4df7-bfb6-5a2790edc570/images/b7765148cd29acf0b2bb837d3e117d538bc85eccd8052e7c5752da4f08d291b7.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        data-testid="hero-section"
      >
        <div className="hero-cyber-content animate-fade-up">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#00FF9D] mb-6">/// Streetwear 2030 ///</p>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl uppercase mb-4 tracking-tighter font-black text-white text-glitch">
            Deine Stadt.
          </h1>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl uppercase mb-8 tracking-tighter font-black text-white">
            Deine <span className="text-[#00FF9D]">Vorwahl.</span>
          </h1>
          <p className="font-body text-lg text-slate-300 mb-12 max-w-lg mx-auto leading-relaxed">
            Premium Streetwear für Menschen, die ihre Heimat lieben. Zeitlos. Authentisch. Lokal.
          </p>
          
          <form onSubmit={handleVorwahlSearch} className="mb-8">
            <input 
              type="text" 
              placeholder="0521" 
              value={vorwahlInput} 
              onChange={(e) => setVorwahlInput(e.target.value)} 
              className="vorwahl-input" 
              maxLength={5} 
              data-testid="vorwahl-input" 
            />
            <p className="font-mono text-xs text-slate-500 mt-4 uppercase tracking-[0.2em]">Gib deine Vorwahl ein</p>
          </form>
          
          <Button onClick={() => vorwahlInput ? navigate(`/vorwahlen?search=${vorwahlInput}`) : navigate('/vorwahlen')} className="btn-neon" data-testid="hero-cta">
            Jetzt entdecken
          </Button>
        </div>
      </section>

      {/* Marquee Ticker */}
      <div className="bg-[#0A0A0A] border-y border-white/5 py-4 overflow-hidden">
        <Marquee gradient={false} speed={50} className="overflow-hidden">
          <span className="font-mono text-sm text-[#00FF9D] uppercase tracking-[0.3em] mx-8">/// VORWAHLLIEBE 2030 ///</span>
          <span className="font-mono text-sm text-slate-500 uppercase tracking-[0.2em] mx-8">DEINE STADT</span>
          <span className="font-mono text-sm text-slate-500 uppercase tracking-[0.2em] mx-8">DEINE VORWAHL</span>
          <span className="font-mono text-sm text-[#00FF9D] uppercase tracking-[0.3em] mx-8">/// PREMIUM STREETWEAR ///</span>
          <span className="font-mono text-sm text-slate-500 uppercase tracking-[0.2em] mx-8">MADE IN EU</span>
          <span className="font-mono text-sm text-[#00FF9D] uppercase tracking-[0.3em] mx-8">/// VORWAHLLIEBE 2030 ///</span>
          <span className="font-mono text-sm text-slate-500 uppercase tracking-[0.2em] mx-8">DEINE STADT</span>
          <span className="font-mono text-sm text-slate-500 uppercase tracking-[0.2em] mx-8">DEINE VORWAHL</span>
          <span className="font-mono text-sm text-[#00FF9D] uppercase tracking-[0.3em] mx-8">/// PREMIUM STREETWEAR ///</span>
          <span className="font-mono text-sm text-slate-500 uppercase tracking-[0.2em] mx-8">MADE IN EU</span>
        </Marquee>
      </div>

      {/* USP Section */}
      <section className="py-12 px-4 md:px-8 bg-[#0A0A0A] border-y border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="usp-item">
              <Package size={20} className="text-[#00FF9D]" />
              <span className="font-body text-sm text-slate-300">Premium Qualität</span>
            </div>
            <div className="usp-item">
              <Truck size={20} className="text-[#00FF9D]" />
              <span className="font-body text-sm text-slate-300">Schneller Versand</span>
            </div>
            <div className="usp-item">
              <CheckCircle size={20} className="text-[#00FF9D]" />
              <span className="font-body text-sm text-slate-300">Made in EU</span>
            </div>
            <div className="usp-item">
              <ShoppingBag size={20} className="text-[#00FF9D]" />
              <span className="font-body text-sm text-slate-300">Sicher bezahlen</span>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 md:py-32 px-4 md:px-8 bg-[#050505]">
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#00FF9D] mb-6">/// Die Idee ///</p>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl uppercase mb-8 font-bold tracking-tight text-white">
            Mehr als nur eine Nummer
          </h2>
          <p className="font-body text-lg text-slate-400 leading-relaxed mb-10">
            Die Vorwahl – sie erinnert an Kindheit, an erste Telefonate, an Heimat. 
            VorwahlLiebe macht aus dieser Verbundenheit zeitlose Mode für alle, 
            die wissen, woher sie kommen.
          </p>
          <Link to="/vorwahlen">
            <Button className="btn-outline">Alle Vorwahlen entdecken</Button>
          </Link>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 md:py-32 px-4 md:px-8 bg-[#0A0A0A]">
        <div className="max-w-5xl mx-auto">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#00FF9D] mb-6 text-center">/// So geht's ///</p>
          <h2 className="font-heading text-3xl md:text-4xl uppercase mb-20 text-center font-bold tracking-tight text-white">In 3 Schritten</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#00FF9D] text-black flex items-center justify-center mx-auto mb-6 neon-glow">
                <span className="font-heading text-2xl font-bold">01</span>
              </div>
              <h3 className="font-heading text-lg uppercase mb-3 font-bold text-white">Vorwahl wählen</h3>
              <p className="font-body text-slate-500">Über 60 Städte und Regionen</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-[#00FF9D] text-black flex items-center justify-center mx-auto mb-6 neon-glow">
                <span className="font-heading text-2xl font-bold">02</span>
              </div>
              <h3 className="font-heading text-lg uppercase mb-3 font-bold text-white">Produkt auswählen</h3>
              <p className="font-body text-slate-500">T-Shirts, Hoodies & mehr</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-[#00FF9D] text-black flex items-center justify-center mx-auto mb-6 neon-glow">
                <span className="font-heading text-2xl font-bold">03</span>
              </div>
              <h3 className="font-heading text-lg uppercase mb-3 font-bold text-white">Heimat tragen</h3>
              <p className="font-body text-slate-500">Zeig, wo dein Herz schlägt</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 px-4 md:px-8 bg-[#050505] border-t border-white/5" data-testid="cta-section">
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#00FF9D] mb-6">/// Jetzt starten ///</p>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl uppercase mb-8 font-bold tracking-tight text-white">Bereit?</h2>
          <p className="font-body text-lg text-slate-500 mb-12">Finde deine Vorwahl und entdecke unser Sortiment.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/shop">
              <Button className="btn-neon px-12">Zum Shop</Button>
            </Link>
            <Link to="/vorwahlen">
              <Button className="btn-outline px-12">Alle Vorwahlen</Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

// ============= PRODUCT CARD =============
const OldProductCard = ({ product, addToCart }) => {
  const navigate = useNavigate();
  const firstVariant = product.variants?.[0];

  return (
    <div className="product-card group cursor-pointer" onClick={() => navigate(`/product/${product.id}`)} data-testid={`product-card-${product.id}`}>
      <div className="aspect-square bg-gray-100 overflow-hidden relative">
        <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" loading="lazy" />
        {product.vorwahl === "0521" && (
          <div className="absolute inset-0 flex items-start justify-center pt-8 pointer-events-none">
            <img src={`/logo-${product.vorwahl}.png`} alt={`I Love ${product.vorwahl}`} className="w-1/2 h-1/2 object-contain" />
          </div>
        )}
        {product.is_new && <Badge className="absolute top-2 left-2 bg-[#FF4500] text-white sharp font-mono text-xs">NEU</Badge>}
        {product.is_bestseller && <Badge className="absolute top-2 right-2 bg-black text-white sharp font-mono text-xs">BESTSELLER</Badge>}
      </div>
      <div className="py-4">
        <p className="font-mono text-xs text-gray-400 uppercase tracking-widest">{product.vorwahl}</p>
        <h3 className="font-body font-semibold mt-1 truncate">{product.name}</h3>
        <p className="font-mono text-sm mt-1">€{firstVariant?.price?.toFixed(2)}</p>
      </div>
    </div>
  );
};

// ============= SPREADSHOP WIDGET =============
// Product data for Cyberpunk Shop - with manual mockup support
// Mockup files should be uploaded to /public/mockups/ with naming:
// - {city}_tshirt_white.png (e.g., dortmund_tshirt_white.png)
// - {city}_hoodie_white.png (e.g., dortmund_hoodie_white.png)

// Function to get mockup URL for a product
const getMockupUrl = (city, productType) => {
  const cityKey = city.toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss');
  const type = productType?.toLowerCase().includes("hoodie") ? "hoodie" : "tshirt";
  return `/mockups/${cityKey}_${type}_white.png`;
};

// Product Mockup Component - Uses Printful mockups or fallback to local/design
const ProductMockup = ({ product, className = "" }) => {
  const [mockupUrl, setMockupUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mockupError, setMockupError] = useState(false);
  
  useEffect(() => {
    const fetchMockup = async () => {
      try {
        // Try to get Printful mockup from API with product_type
        const response = await axios.get(`${API}/printful/mockups/by-design`, {
          params: { 
            design_url: product.designImage,
            product_type: product.printfulProductType || "tshirt"
          }
        });
        
        if (response.data?.mockup_url) {
          setMockupUrl(response.data.mockup_url);
        } else {
          // Fallback to local mockup path
          const localUrl = getMockupUrl(product.city, product.type);
          setMockupUrl(localUrl);
        }
      } catch (error) {
        // Fallback to local mockup path
        const localUrl = getMockupUrl(product.city, product.type);
        setMockupUrl(localUrl);
      } finally {
        setLoading(false);
      }
    };
    
    fetchMockup();
  }, [product.designImage, product.city, product.type, product.printfulProductType]);
  
  // Loading state
  if (loading) {
    return (
      <div className={`relative bg-gradient-to-br from-[#0A0A0A] to-[#111] flex items-center justify-center ${className}`}>
        <div className="animate-pulse">
          <div className="w-24 h-24 bg-slate-800 rounded"></div>
        </div>
      </div>
    );
  }
  
  // If mockup file doesn't exist, show the design image
  if (mockupError || !mockupUrl) {
    return (
      <div className={`relative bg-gradient-to-br from-[#0A0A0A] to-[#111] flex items-center justify-center ${className}`}>
        <img 
          src={product.designImage} 
          alt={product.name}
          className="w-3/4 h-3/4 object-contain drop-shadow-[0_0_30px_rgba(0,255,157,0.3)]"
        />
        <div className="absolute bottom-2 right-2 bg-black/60 px-2 py-1 rounded">
          <span className="font-mono text-[10px] text-slate-400">Preview</span>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`relative ${className}`}>
      <img 
        src={mockupUrl} 
        alt={`${product.name} ${product.type} Mockup`}
        className="w-full h-full object-cover"
        onError={() => setMockupError(true)}
      />
    </div>
  );
};

const PRODUCTS = [
  // ===== DORTMUND 0231 =====
  {
    id: "dortmund-tshirt-001",
    name: "I ❤️ 0231",
    city: "Dortmund",
    vorwahl: "0231",
    type: "Heavyweight T-Shirt",
    price: 34.90,
    description: "Premium Garment-Dyed Heavyweight T-Shirt. Comfort Colors 1717 - das beliebteste Shirt für Streetwear.",
    designImage: "https://customer-assets.emergentagent.com/job_1ed892fa-e1a3-4df7-bfb6-5a2790edc570/artifacts/425a8i1k_Dortmund%200231.png",
    colors: ["Weiß", "Grau", "Schwarz"],
    sizes: ["S", "M", "L", "XL", "2XL"],
    printful_product_id: 586,
    printful_variant_id: 15125,
    printfulProductType: "tshirt",
    category: "t-shirts"
  },
  {
    id: "dortmund-hoodie-001",
    name: "I ❤️ 0231",
    city: "Dortmund",
    vorwahl: "0231",
    type: "Premium Hoodie",
    price: 54.90,
    description: "Premium Pullover Hoodie von Cotton Heritage M2580. Extra weich und warm.",
    designImage: "https://customer-assets.emergentagent.com/job_1ed892fa-e1a3-4df7-bfb6-5a2790edc570/artifacts/425a8i1k_Dortmund%200231.png",
    colors: ["Weiß", "Grau", "Schwarz"],
    sizes: ["S", "M", "L", "XL", "2XL"],
    printful_product_id: 380,
    printful_variant_id: 10775,
    printfulProductType: "hoodie",
    category: "hoodies"
  },
  {
    id: "dortmund-sweatshirt-001",
    name: "I ❤️ 0231",
    city: "Dortmund",
    vorwahl: "0231",
    type: "Crewneck Sweatshirt",
    price: 44.90,
    description: "Klassisches Rundhals-Sweatshirt Gildan 18000. Perfekt für kühle Tage.",
    designImage: "https://customer-assets.emergentagent.com/job_1ed892fa-e1a3-4df7-bfb6-5a2790edc570/artifacts/425a8i1k_Dortmund%200231.png",
    colors: ["Weiß", "Grau", "Schwarz"],
    sizes: ["S", "M", "L", "XL", "2XL"],
    printful_product_id: 145,
    printful_variant_id: 5427,
    printfulProductType: "sweatshirt",
    category: "sweatshirts"
  },
  {
    id: "dortmund-longsleeve-001",
    name: "I ❤️ 0231",
    city: "Dortmund",
    vorwahl: "0231",
    type: "Long Sleeve",
    price: 39.90,
    description: "Langarm-Shirt Bella + Canvas 3501. Perfekte Passform und Tragekomfort.",
    designImage: "https://customer-assets.emergentagent.com/job_1ed892fa-e1a3-4df7-bfb6-5a2790edc570/artifacts/425a8i1k_Dortmund%200231.png",
    colors: ["Weiß", "Grau", "Schwarz"],
    sizes: ["S", "M", "L", "XL", "2XL"],
    printful_product_id: 356,
    printful_variant_id: 10143,
    printfulProductType: "longsleeve",
    category: "t-shirts"
  },
  {
    id: "dortmund-tank-001",
    name: "I ❤️ 0231",
    city: "Dortmund",
    vorwahl: "0231",
    type: "Tank Top",
    price: 29.90,
    description: "Leichtes Tank Top Bella + Canvas 3480. Perfekt für warme Tage.",
    designImage: "https://customer-assets.emergentagent.com/job_1ed892fa-e1a3-4df7-bfb6-5a2790edc570/artifacts/425a8i1k_Dortmund%200231.png",
    colors: ["Weiß", "Grau", "Schwarz"],
    sizes: ["S", "M", "L", "XL", "2XL"],
    printful_product_id: 248,
    printful_variant_id: 8660,
    printfulProductType: "tank",
    category: "t-shirts"
  },
  {
    id: "dortmund-vintage-sweat-001",
    name: "I ❤️ 0231",
    city: "Dortmund",
    vorwahl: "0231",
    type: "Vintage Sweatshirt",
    price: 49.90,
    description: "Garment-Dyed Vintage Sweatshirt Comfort Colors 1566. Einzigartiger Used-Look.",
    designImage: "https://customer-assets.emergentagent.com/job_1ed892fa-e1a3-4df7-bfb6-5a2790edc570/artifacts/425a8i1k_Dortmund%200231.png",
    colors: ["Weiß", "Grau", "Schwarz"],
    sizes: ["S", "M", "L", "XL", "2XL"],
    printful_product_id: 839,
    printful_variant_id: 22011,
    printfulProductType: "garment_sweat",
    category: "sweatshirts"
  },
  {
    id: "dortmund-heavy-longsleeve-001",
    name: "I ❤️ 0231",
    city: "Dortmund",
    vorwahl: "0231",
    type: "Heavyweight Long Sleeve",
    price: 42.90,
    description: "Heavyweight Garment-Dyed Long Sleeve Comfort Colors 6014. Premium Qualität.",
    designImage: "https://customer-assets.emergentagent.com/job_1ed892fa-e1a3-4df7-bfb6-5a2790edc570/artifacts/425a8i1k_Dortmund%200231.png",
    colors: ["Weiß", "Grau", "Schwarz"],
    sizes: ["S", "M", "L", "XL", "2XL"],
    printful_product_id: 753,
    printful_variant_id: 19425,
    printfulProductType: "longsleeve_cc",
    category: "t-shirts"
  },
  {
    id: "dortmund-gildan-hoodie-001",
    name: "I ❤️ 0231",
    city: "Dortmund",
    vorwahl: "0231",
    type: "Heavy Blend Hoodie",
    price: 44.90,
    description: "Gildan 18500 Heavy Blend Hoodie. Der Klassiker - warm und langlebig.",
    designImage: "https://customer-assets.emergentagent.com/job_1ed892fa-e1a3-4df7-bfb6-5a2790edc570/artifacts/425a8i1k_Dortmund%200231.png",
    colors: ["Weiß", "Grau", "Schwarz"],
    sizes: ["S", "M", "L", "XL", "2XL"],
    printful_product_id: 146,
    printful_variant_id: 5523,
    printfulProductType: "gildan_hoodie",
    category: "hoodies"
  },
  // ===== DORTMUND ACCESSOIRES =====
  {
    id: "dortmund-cap-001",
    name: "I ❤️ 0231",
    city: "Dortmund",
    vorwahl: "0231",
    type: "Dad Hat",
    price: 24.90,
    description: "Klassische Distressed Dad Hat in Charcoal Grey. Entspannter Look mit verstellbarem Riemen.",
    designImage: "https://customer-assets.emergentagent.com/job_1ed892fa-e1a3-4df7-bfb6-5a2790edc570/artifacts/425a8i1k_Dortmund%200231.png",
    colors: ["Grau"],
    sizes: ["One Size"],
    printful_product_id: 396,
    printful_variant_id: 10992,
    printfulProductType: "cap",
    category: "caps"
  },
  {
    id: "dortmund-snapback-001",
    name: "I ❤️ 0231",
    city: "Dortmund",
    vorwahl: "0231",
    type: "Snapback Cap",
    price: 27.90,
    description: "Klassische Snapback Cap in Weiß mit flacher Krempe. Street Style.",
    designImage: "https://customer-assets.emergentagent.com/job_1ed892fa-e1a3-4df7-bfb6-5a2790edc570/artifacts/425a8i1k_Dortmund%200231.png",
    colors: ["Weiß", "Grau"],
    sizes: ["One Size"],
    printful_product_id: 77,
    printful_variant_id: 4473,
    printfulProductType: "snapback",
    category: "caps"
  },
  {
    id: "dortmund-trucker-001",
    name: "I ❤️ 0231",
    city: "Dortmund",
    vorwahl: "0231",
    type: "Trucker Cap",
    price: 24.90,
    description: "Richardson Trucker Cap in Heather Grey/Weiß mit Meshback. Atmungsaktiv und stylish.",
    designImage: "https://customer-assets.emergentagent.com/job_1ed892fa-e1a3-4df7-bfb6-5a2790edc570/artifacts/425a8i1k_Dortmund%200231.png",
    colors: ["Grau/Weiß"],
    sizes: ["One Size"],
    printful_product_id: 422,
    printful_variant_id: 16712,
    printfulProductType: "trucker",
    category: "caps"
  },
  {
    id: "dortmund-beanie-001",
    name: "I ❤️ 0231",
    city: "Dortmund",
    vorwahl: "0231",
    type: "Beanie",
    price: 22.90,
    description: "Warme Strickmütze in Weiß für kalte Tage. Klassisches Design.",
    designImage: "https://customer-assets.emergentagent.com/job_1ed892fa-e1a3-4df7-bfb6-5a2790edc570/artifacts/425a8i1k_Dortmund%200231.png",
    colors: ["Weiß", "Grau"],
    sizes: ["One Size"],
    printful_product_id: 81,
    printful_variant_id: 4525,
    printfulProductType: "beanie",
    category: "caps"
  },
  {
    id: "dortmund-tote-001",
    name: "I ❤️ 0231",
    city: "Dortmund",
    vorwahl: "0231",
    type: "Stoffbeutel",
    price: 19.90,
    description: "Organic Cotton Stoffbeutel in Natur. Nachhaltig und praktisch.",
    designImage: "https://customer-assets.emergentagent.com/job_1ed892fa-e1a3-4df7-bfb6-5a2790edc570/artifacts/425a8i1k_Dortmund%200231.png",
    colors: ["Natur"],
    sizes: ["One Size"],
    printful_product_id: 367,
    printful_variant_id: 10458,
    printfulProductType: "tote",
    category: "bags"
  },
  {
    id: "dortmund-socks-001",
    name: "I ❤️ 0231",
    city: "Dortmund",
    vorwahl: "0231",
    type: "Socken",
    price: 14.90,
    description: "Sublimierte Socken mit durchgehendem Design auf weißer Basis. Bequem und stylish.",
    designImage: "https://customer-assets.emergentagent.com/job_1ed892fa-e1a3-4df7-bfb6-5a2790edc570/artifacts/425a8i1k_Dortmund%200231.png",
    colors: ["Weiß"],
    sizes: ["M", "L", "XL"],
    printful_product_id: 186,
    printful_variant_id: 7290,
    printfulProductType: "socks",
    category: "accessories"
  },
  {
    id: "dortmund-iphone-001",
    name: "I ❤️ 0231",
    city: "Dortmund",
    vorwahl: "0231",
    type: "iPhone Hülle",
    price: 19.90,
    description: "Kristallklare iPhone Hülle mit deinem Vorwahl-Design.",
    designImage: "https://customer-assets.emergentagent.com/job_1ed892fa-e1a3-4df7-bfb6-5a2790edc570/artifacts/425a8i1k_Dortmund%200231.png",
    colors: ["Transparent"],
    sizes: ["iPhone 11", "iPhone 12", "iPhone 13", "iPhone 14", "iPhone 15"],
    printful_product_id: 181,
    printful_variant_id: 10994,
    printfulProductType: "iphone_case",
    category: "accessories"
  },
  {
    id: "dortmund-mousepad-001",
    name: "I ❤️ 0231",
    city: "Dortmund",
    vorwahl: "0231",
    type: "Mauspad",
    price: 14.90,
    description: "Premium Mauspad für dein Home Office. Rutschfest und präzise.",
    designImage: "https://customer-assets.emergentagent.com/job_1ed892fa-e1a3-4df7-bfb6-5a2790edc570/artifacts/425a8i1k_Dortmund%200231.png",
    colors: ["Weiß"],
    sizes: ["Standard"],
    printful_product_id: 518,
    printful_variant_id: 13097,
    printfulProductType: "mousepad",
    category: "accessories"
  },
  // ===== AACHEN 0241 =====
  {
    id: "aachen-tshirt-001",
    name: "I ❤️ 0241",
    city: "Aachen",
    vorwahl: "0241",
    type: "Heavyweight T-Shirt",
    price: 34.90,
    description: "Premium Garment-Dyed Heavyweight T-Shirt für echte Öcher.",
    designImage: "https://customer-assets.emergentagent.com/job_1ed892fa-e1a3-4df7-bfb6-5a2790edc570/artifacts/n8oas3q2_Aachen%200241.png",
    colors: ["Weiß", "Grau", "Schwarz"],
    sizes: ["S", "M", "L", "XL", "2XL"],
    printful_product_id: 586,
    printful_variant_id: 15125,
    printfulProductType: "tshirt",
    category: "t-shirts"
  },
  {
    id: "aachen-hoodie-001",
    name: "I ❤️ 0241",
    city: "Aachen",
    vorwahl: "0241",
    type: "Premium Hoodie",
    price: 54.90,
    description: "Premium Pullover Hoodie für echte Öcher. Kuschelig warm.",
    designImage: "https://customer-assets.emergentagent.com/job_1ed892fa-e1a3-4df7-bfb6-5a2790edc570/artifacts/n8oas3q2_Aachen%200241.png",
    colors: ["Weiß", "Grau", "Schwarz"],
    sizes: ["S", "M", "L", "XL", "2XL"],
    printful_product_id: 380,
    printful_variant_id: 10775,
    printfulProductType: "hoodie",
    category: "hoodies"
  },
  // AUE
  {
    id: "aue-tshirt-001",
    name: "I ❤️ 03771",
    city: "Aue",
    vorwahl: "03771",
    type: "Streetwear T-Shirt",
    price: 29.90,
    description: "Premium Oversized T-Shirt für Erzgebirgler. Die Veilchen-Farben Lila-Weiß.",
    designImage: "https://customer-assets.emergentagent.com/job_1ed892fa-e1a3-4df7-bfb6-5a2790edc570/artifacts/yplgdbpe_Aue%2003771.png",
    colors: ["Weiß", "Grau"],
    sizes: ["S", "M", "L", "XL", "XXL"],
    printful_variant_id: 4012,
    category: "t-shirts"
  },
  {
    id: "aue-hoodie-001",
    name: "I ❤️ 03771",
    city: "Aue",
    vorwahl: "03771",
    type: "Organic Hoodie",
    price: 49.90,
    description: "Premium Organic Hoodie für Erzgebirgler. Kuschelig warm.",
    designImage: "https://customer-assets.emergentagent.com/job_1ed892fa-e1a3-4df7-bfb6-5a2790edc570/artifacts/yplgdbpe_Aue%2003771.png",
    colors: ["Weiß", "Grau"],
    sizes: ["S", "M", "L", "XL", "XXL"],
    printful_variant_id: 3312,
    category: "hoodies"
  },
  // AUGSBURG
  {
    id: "augsburg-tshirt-001",
    name: "I ❤️ 0821",
    city: "Augsburg",
    vorwahl: "0821",
    type: "Streetwear T-Shirt",
    price: 29.90,
    description: "Premium Oversized T-Shirt für Fuggerstädter. FCA-Farben Rot-Grün-Weiß.",
    designImage: "https://customer-assets.emergentagent.com/job_1ed892fa-e1a3-4df7-bfb6-5a2790edc570/artifacts/oszthaw3_Augsburg%200821.png",
    colors: ["Weiß", "Grau"],
    sizes: ["S", "M", "L", "XL", "XXL"],
    printful_variant_id: 4012,
    category: "t-shirts"
  },
  {
    id: "augsburg-hoodie-001",
    name: "I ❤️ 0821",
    city: "Augsburg",
    vorwahl: "0821",
    type: "Organic Hoodie",
    price: 49.90,
    description: "Premium Organic Hoodie für Fuggerstädter. Kuschelig warm.",
    designImage: "https://customer-assets.emergentagent.com/job_1ed892fa-e1a3-4df7-bfb6-5a2790edc570/artifacts/oszthaw3_Augsburg%200821.png",
    colors: ["Weiß", "Grau"],
    sizes: ["S", "M", "L", "XL", "XXL"],
    printful_variant_id: 3312,
    category: "hoodies"
  },
  // BIELEFELD
  {
    id: "bielefeld-tshirt-001",
    name: "I ❤️ 0521",
    city: "Bielefeld",
    vorwahl: "0521",
    type: "Streetwear T-Shirt",
    price: 29.90,
    description: "Premium Oversized T-Shirt für Ostwestfalen. Arminia-Farben Blau-Weiß-Schwarz.",
    designImage: "https://customer-assets.emergentagent.com/job_1ed892fa-e1a3-4df7-bfb6-5a2790edc570/artifacts/vupfospm_Bielefeld%200521.png",
    colors: ["Weiß", "Grau"],
    sizes: ["S", "M", "L", "XL", "XXL"],
    printful_variant_id: 4012,
    category: "t-shirts"
  },
  {
    id: "bielefeld-hoodie-001",
    name: "I ❤️ 0521",
    city: "Bielefeld",
    vorwahl: "0521",
    type: "Organic Hoodie",
    price: 49.90,
    description: "Premium Organic Hoodie für Ostwestfalen. Kuschelig warm.",
    designImage: "https://customer-assets.emergentagent.com/job_1ed892fa-e1a3-4df7-bfb6-5a2790edc570/artifacts/vupfospm_Bielefeld%200521.png",
    colors: ["Weiß", "Grau"],
    sizes: ["S", "M", "L", "XL", "XXL"],
    printful_variant_id: 3312,
    category: "hoodies"
  },
  // BOCHUM
  {
    id: "bochum-tshirt-001",
    name: "I ❤️ 0234",
    city: "Bochum",
    vorwahl: "0234",
    type: "Streetwear T-Shirt",
    price: 29.90,
    description: "Premium Oversized T-Shirt für Ruhris. VfL-Farben Blau-Weiß.",
    designImage: "https://customer-assets.emergentagent.com/job_1ed892fa-e1a3-4df7-bfb6-5a2790edc570/artifacts/yr5e9y5q_Bochum%200234.png",
    colors: ["Weiß", "Grau"],
    sizes: ["S", "M", "L", "XL", "XXL"],
    printful_variant_id: 4012,
    category: "t-shirts"
  },
  {
    id: "bochum-hoodie-001",
    name: "I ❤️ 0234",
    city: "Bochum",
    vorwahl: "0234",
    type: "Organic Hoodie",
    price: 49.90,
    description: "Premium Organic Hoodie für Ruhris. Kuschelig warm.",
    designImage: "https://customer-assets.emergentagent.com/job_1ed892fa-e1a3-4df7-bfb6-5a2790edc570/artifacts/yr5e9y5q_Bochum%200234.png",
    colors: ["Weiß", "Grau"],
    sizes: ["S", "M", "L", "XL", "XXL"],
    printful_variant_id: 3312,
    category: "hoodies"
  }
];

// Product Card Component
const ProductCard = ({ product, onClick }) => {
  const [hovered, setHovered] = useState(false);
  
  return (
    <div 
      className="group cursor-pointer"
      onClick={() => onClick(product)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative bg-[#0A0A0A] border border-white/10 overflow-hidden transition-all duration-500 hover:border-[#00FF9D] hover:shadow-[0_0_50px_rgba(0,255,157,0.3)]">
        {/* Product Image */}
        <div className="relative aspect-square overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,255,157,0.1)_0%,transparent_70%)] opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10"></div>
          
          {/* Show mockup by default, design on hover */}
          <div className={`absolute inset-0 transition-opacity duration-500 ${hovered ? 'opacity-0' : 'opacity-100'}`}>
            <ProductMockup 
              product={product}
              className="w-full h-full"
            />
          </div>
          <div className={`absolute inset-0 transition-opacity duration-500 ${hovered ? 'opacity-100' : 'opacity-0'} p-8 flex items-center justify-center bg-gradient-to-br from-[#0A0A0A] to-[#111]`}>
            <img 
              src={product.designImage} 
              alt={product.name}
              className="w-full h-full object-contain transform group-hover:scale-105 transition-transform duration-500 drop-shadow-[0_0_20px_rgba(0,255,157,0.3)]"
            />
          </div>
          
          {/* Floating Badge */}
          <div className="absolute top-4 left-4 z-20">
            <span className="font-mono text-xs bg-[#00FF9D] text-black px-3 py-1 uppercase tracking-wider font-bold">
              {product.vorwahl}
            </span>
          </div>
        </div>
        
        {/* Product Info */}
        <div className="p-6 border-t border-white/5">
          <p className="font-mono text-xs text-[#00FF9D] uppercase tracking-[0.2em] mb-2">{product.city}</p>
          <h3 className="font-heading text-xl font-bold text-white uppercase mb-1 group-hover:text-[#00FF9D] transition-colors">{product.name}</h3>
          <p className="font-body text-sm text-slate-500 mb-4">{product.type}</p>
          <div className="flex items-center justify-between">
            <span className="font-heading text-2xl font-bold text-white">€{product.price.toFixed(2)}</span>
            <span className="font-mono text-xs text-slate-500 uppercase">Ab {product.sizes[0]}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Product Detail Modal
const ProductModal = ({ product, isOpen, onClose }) => {
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [isOrdering, setIsOrdering] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  
  // Checkout form state
  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    street: "",
    postcode: "",
    city: "",
    country: "DE"
  });

  if (!product) return null;

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleProceedToCheckout = () => {
    if (!selectedSize || !selectedColor) {
      toast.error("Bitte wähle Größe und Farbe");
      return;
    }
    setShowCheckout(true);
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.firstname || !formData.lastname || !formData.email || !formData.street || !formData.postcode || !formData.city) {
      toast.error("Bitte fülle alle Felder aus");
      return;
    }
    
    setIsOrdering(true);
    
    try {
      const orderData = {
        items: [{
          product_id: product.id,
          design_url: product.designImage,
          printful_variant_id: product.printful_variant_id || 4012,
          size: selectedSize,
          color: selectedColor,
          quantity: 1
        }],
        delivery: {
          firstname: formData.firstname,
          lastname: formData.lastname,
          email: formData.email,
          street: formData.street,
          postcode: formData.postcode,
          city: formData.city,
          country: formData.country
        }
      };
      
      const response = await axios.post(`${API}/printful/orders`, orderData);
      
      if (response.data.success) {
        setOrderSuccess(true);
        toast.success("Bestellung erfolgreich! 🎉");
      }
    } catch (error) {
      console.error("Order error:", error);
      toast.error(error.response?.data?.detail || "Fehler bei der Bestellung");
    } finally {
      setIsOrdering(false);
    }
  };

  const resetModal = () => {
    setShowCheckout(false);
    setOrderSuccess(false);
    setSelectedSize("");
    setSelectedColor("");
    setFormData({
      firstname: "",
      lastname: "",
      email: "",
      street: "",
      postcode: "",
      city: "",
      country: "DE"
    });
    onClose();
  };

  // Success Screen
  if (orderSuccess) {
    return (
      <Dialog open={isOpen} onOpenChange={resetModal}>
        <DialogContent className="max-w-md bg-[#0A0A0A] border border-[#00FF9D]/30 p-8 text-center">
          <div className="py-8">
            <div className="w-20 h-20 bg-[#00FF9D]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-[#00FF9D]" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-white uppercase mb-4">Bestellung erfolgreich!</h2>
            <p className="font-body text-slate-400 mb-6">
              Deine Bestellung wurde an Printful übermittelt. Du erhältst eine Bestätigung per E-Mail.
            </p>
            <p className="font-mono text-xs text-[#00FF9D] mb-8">
              Lieferzeit: 2-5 Werktage
            </p>
            <Button onClick={resetModal} className="btn-neon w-full">
              Weiter shoppen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Checkout Form
  if (showCheckout) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-lg bg-[#0A0A0A] border border-white/10 p-0 overflow-hidden max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-white/10">
            <button onClick={() => setShowCheckout(false)} className="text-slate-500 hover:text-white mb-4 flex items-center gap-2">
              <ChevronRight size={16} className="rotate-180" /> Zurück
            </button>
            <h2 className="font-heading text-2xl font-bold text-white uppercase">Checkout</h2>
          </div>
          
          {/* Order Summary with Mockup */}
          <div className="p-6 bg-[#050505] border-b border-white/10">
            <div className="flex gap-4">
              <div className="w-24 h-24 overflow-hidden rounded bg-[#0A0A0A]">
                <ProductMockup 
                  product={product}
                  className="w-full h-full"
                />
              </div>
              <div>
                <p className="font-mono text-xs text-[#00FF9D]">{product.vorwahl} · {product.city}</p>
                <p className="font-heading text-lg font-bold text-white">{product.name}</p>
                <p className="font-body text-sm text-slate-500">{product.type} · {selectedSize} · {selectedColor}</p>
                <p className="font-heading text-lg font-bold text-[#00FF9D] mt-1">€{product.price.toFixed(2)}</p>
              </div>
            </div>
          </div>
          
          {/* Shipping Form */}
          <form onSubmit={handleSubmitOrder} className="p-6 space-y-4">
            <p className="font-mono text-xs text-slate-400 uppercase tracking-wider mb-4">Lieferadresse</p>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="font-body text-sm text-slate-400 mb-1 block">Vorname *</label>
                <Input 
                  name="firstname" 
                  value={formData.firstname} 
                  onChange={handleInputChange}
                  className="bg-[#050505] border-white/10 text-white"
                  required
                />
              </div>
              <div>
                <label className="font-body text-sm text-slate-400 mb-1 block">Nachname *</label>
                <Input 
                  name="lastname" 
                  value={formData.lastname} 
                  onChange={handleInputChange}
                  className="bg-[#050505] border-white/10 text-white"
                  required
                />
              </div>
            </div>
            
            <div>
              <label className="font-body text-sm text-slate-400 mb-1 block">E-Mail *</label>
              <Input 
                name="email" 
                type="email"
                value={formData.email} 
                onChange={handleInputChange}
                className="bg-[#050505] border-white/10 text-white"
                required
              />
            </div>
            
            <div>
              <label className="font-body text-sm text-slate-400 mb-1 block">Straße & Hausnummer *</label>
              <Input 
                name="street" 
                value={formData.street} 
                onChange={handleInputChange}
                className="bg-[#050505] border-white/10 text-white"
                required
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="font-body text-sm text-slate-400 mb-1 block">PLZ *</label>
                <Input 
                  name="postcode" 
                  value={formData.postcode} 
                  onChange={handleInputChange}
                  className="bg-[#050505] border-white/10 text-white"
                  required
                />
              </div>
              <div className="col-span-2">
                <label className="font-body text-sm text-slate-400 mb-1 block">Stadt *</label>
                <Input 
                  name="city" 
                  value={formData.city} 
                  onChange={handleInputChange}
                  className="bg-[#050505] border-white/10 text-white"
                  required
                />
              </div>
            </div>
            
            {/* Price Summary */}
            <div className="pt-4 border-t border-white/10 space-y-2">
              <div className="flex justify-between font-body text-slate-400">
                <span>Artikel</span>
                <span>€{product.price.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-body text-slate-400">
                <span>Versand</span>
                <span>€4.99</span>
              </div>
              <div className="flex justify-between font-heading text-xl font-bold text-white pt-2 border-t border-white/10">
                <span>Gesamt</span>
                <span className="text-[#00FF9D]">€{(product.price + 4.99).toFixed(2)}</span>
              </div>
            </div>
            
            <Button 
              type="submit"
              disabled={isOrdering}
              className="btn-neon w-full py-6 text-lg neon-glow mt-6"
            >
              {isOrdering ? "Wird verarbeitet..." : "Zahlungspflichtig bestellen"}
            </Button>
            
            <p className="font-mono text-xs text-slate-500 text-center mt-4">
              Zahlung erfolgt per Rechnung nach Lieferung
            </p>
          </form>
        </DialogContent>
      </Dialog>
    );
  }

  // Product View (original)
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl bg-[#0A0A0A] border border-white/10 p-0 overflow-hidden">
        <div className="grid md:grid-cols-2">
          {/* Product Mockup */}
          <div className="relative bg-gradient-to-br from-[#050505] to-[#111] flex items-center justify-center overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,255,157,0.15)_0%,transparent_60%)]"></div>
            <ProductMockup 
              product={product}
              className="w-full h-full"
            />
          </div>
          
          {/* Product Details */}
          <div className="p-8 flex flex-col">
            <div className="mb-2">
              <span className="font-mono text-xs bg-[#00FF9D] text-black px-3 py-1 uppercase tracking-wider font-bold">
                {product.vorwahl} · {product.city}
              </span>
            </div>
            
            <h2 className="font-heading text-3xl font-black text-white uppercase mb-2">{product.name}</h2>
            <p className="font-body text-slate-400 mb-1">{product.type}</p>
            <p className="font-heading text-3xl font-bold text-[#00FF9D] mb-6">€{product.price.toFixed(2)}</p>
            
            <p className="font-body text-slate-500 mb-8">{product.description}</p>
            
            {/* Size Selection */}
            <div className="mb-6">
              <label className="font-mono text-xs text-slate-400 uppercase tracking-wider mb-3 block">Größe</label>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map(size => (
                  <button
                    key={size}
                    onClick={() => setSelectedSize(size)}
                    className={`px-4 py-2 border font-mono text-sm transition-all ${
                      selectedSize === size 
                        ? 'bg-[#00FF9D] text-black border-[#00FF9D]' 
                        : 'border-white/20 text-white hover:border-[#00FF9D]'
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Color Selection */}
            <div className="mb-8">
              <label className="font-mono text-xs text-slate-400 uppercase tracking-wider mb-3 block">Farbe</label>
              <div className="flex flex-wrap gap-2">
                {product.colors.map(color => (
                  <button
                    key={color}
                    onClick={() => setSelectedColor(color)}
                    className={`px-4 py-2 border font-mono text-sm transition-all ${
                      selectedColor === color 
                        ? 'bg-[#00FF9D] text-black border-[#00FF9D]' 
                        : 'border-white/20 text-white hover:border-[#00FF9D]'
                    }`}
                  >
                    {color}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Order Button */}
            <Button 
              onClick={handleProceedToCheckout}
              className="btn-neon w-full py-6 text-lg neon-glow"
              data-testid="order-button"
            >
              Jetzt bestellen
            </Button>
            
            {/* USPs */}
            <div className="mt-6 grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2 text-slate-500">
                <Package size={16} className="text-[#00FF9D]" />
                <span className="font-body text-xs">Premium Qualität</span>
              </div>
              <div className="flex items-center gap-2 text-slate-500">
                <Truck size={16} className="text-[#00FF9D]" />
                <span className="font-body text-xs">2-5 Tage Lieferung</span>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const SpreadshopWidget = () => {
  const [searchParams] = useSearchParams();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [filter, setFilter] = useState("all");
  
  // Get vorwahl filter from URL params
  const vorwahlFilter = searchParams.get("vorwahl") || "";
  
  // Filter by category first, then by vorwahl if present
  const filteredProducts = PRODUCTS.filter(p => {
    const matchesCategory = filter === "all" || p.category === filter;
    const matchesVorwahl = !vorwahlFilter || p.vorwahl === vorwahlFilter;
    return matchesCategory && matchesVorwahl;
  });

  // Get the city name for the header if filtering by vorwahl
  const filteredCity = vorwahlFilter ? PRODUCTS.find(p => p.vorwahl === vorwahlFilter)?.city : null;

  return (
    <main className="min-h-screen bg-[#050505]" data-testid="shop-page">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(0,255,157,0.15)_0%,transparent_50%)]"></div>
        
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <p className="font-mono text-xs tracking-[0.3em] text-[#00FF9D] uppercase mb-6 animate-fade-up">
            {vorwahlFilter ? `/// ${vorwahlFilter} ///` : "/// Kollektion 2030 ///"}
          </p>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-8xl font-black mb-6 text-white tracking-tighter uppercase animate-fade-up text-glitch">
            {filteredCity || "SHOP"}
          </h1>
          <p className="font-body text-lg md:text-xl text-slate-400 mb-8 max-w-2xl mx-auto animate-fade-up">
            {vorwahlFilter 
              ? `Alle Produkte für die Vorwahl ${vorwahlFilter}` 
              : "Premium Streetwear mit deiner Vorwahl. Hochwertige Materialien, nachhaltig produziert in der EU."}
          </p>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="px-6 md:px-8 py-8 border-y border-white/5 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-center gap-4">
          <button 
            onClick={() => setFilter("all")}
            className={`font-mono text-sm uppercase tracking-wider px-6 py-2 border transition-all ${
              filter === "all" ? "bg-[#00FF9D] text-black border-[#00FF9D]" : "border-white/20 text-white hover:border-[#00FF9D]"
            }`}
          >
            Alle
          </button>
          <button 
            onClick={() => setFilter("t-shirts")}
            className={`font-mono text-sm uppercase tracking-wider px-6 py-2 border transition-all ${
              filter === "t-shirts" ? "bg-[#00FF9D] text-black border-[#00FF9D]" : "border-white/20 text-white hover:border-[#00FF9D]"
            }`}
          >
            T-Shirts
          </button>
          <button 
            onClick={() => setFilter("hoodies")}
            className={`font-mono text-sm uppercase tracking-wider px-6 py-2 border transition-all ${
              filter === "hoodies" ? "bg-[#00FF9D] text-black border-[#00FF9D]" : "border-white/20 text-white hover:border-[#00FF9D]"
            }`}
          >
            Hoodies
          </button>
          <button 
            onClick={() => setFilter("sweatshirts")}
            className={`font-mono text-sm uppercase tracking-wider px-6 py-2 border transition-all ${
              filter === "sweatshirts" ? "bg-[#00FF9D] text-black border-[#00FF9D]" : "border-white/20 text-white hover:border-[#00FF9D]"
            }`}
          >
            Sweatshirts
          </button>
          <button 
            onClick={() => setFilter("caps")}
            className={`font-mono text-sm uppercase tracking-wider px-6 py-2 border transition-all ${
              filter === "caps" ? "bg-[#00FF9D] text-black border-[#00FF9D]" : "border-white/20 text-white hover:border-[#00FF9D]"
            }`}
          >
            Caps
          </button>
          <button 
            onClick={() => setFilter("bags")}
            className={`font-mono text-sm uppercase tracking-wider px-6 py-2 border transition-all ${
              filter === "bags" ? "bg-[#00FF9D] text-black border-[#00FF9D]" : "border-white/20 text-white hover:border-[#00FF9D]"
            }`}
          >
            Taschen
          </button>
          <button 
            onClick={() => setFilter("accessories")}
            className={`font-mono text-sm uppercase tracking-wider px-6 py-2 border transition-all ${
              filter === "accessories" ? "bg-[#00FF9D] text-black border-[#00FF9D]" : "border-white/20 text-white hover:border-[#00FF9D]"
            }`}
          >
            Zubehör
          </button>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 md:py-24 px-6 md:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProducts.map(product => (
              <ProductCard 
                key={product.id} 
                product={product} 
                onClick={setSelectedProduct}
              />
            ))}
          </div>
          
          {filteredProducts.length === 0 && (
            <div className="text-center py-20">
              <p className="font-mono text-slate-500">Keine Produkte gefunden</p>
            </div>
          )}
        </div>
      </section>

      {/* Product Modal */}
      <ProductModal 
        product={selectedProduct}
        isOpen={!!selectedProduct}
        onClose={() => setSelectedProduct(null)}
      />

      {/* Features */}
      <section className="py-24 px-6 md:px-8 bg-[#0A0A0A] border-y border-white/5">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
            <div>
              <div className="w-16 h-16 bg-[#00FF9D]/10 border border-[#00FF9D]/30 flex items-center justify-center mx-auto mb-6">
                <Package size={28} className="text-[#00FF9D]" />
              </div>
              <h3 className="font-heading text-lg font-bold text-white uppercase mb-3">Premium Qualität</h3>
              <p className="font-body text-slate-500">Hochwertige Textilien mit erstklassigem Druck</p>
            </div>
            <div>
              <div className="w-16 h-16 bg-[#00FF9D]/10 border border-[#00FF9D]/30 flex items-center justify-center mx-auto mb-6">
                <Truck size={28} className="text-[#00FF9D]" />
              </div>
              <h3 className="font-heading text-lg font-bold text-white uppercase mb-3">Schneller Versand</h3>
              <p className="font-body text-slate-500">On-Demand Produktion in 2-5 Werktagen</p>
            </div>
            <div>
              <div className="w-16 h-16 bg-[#00FF9D]/10 border border-[#00FF9D]/30 flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={28} className="text-[#00FF9D]" />
              </div>
              <h3 className="font-heading text-lg font-bold text-white uppercase mb-3">Made in EU</h3>
              <p className="font-body text-slate-500">Nachhaltig & fair produziert</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 md:px-8 bg-[#050505]">
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-mono text-xs tracking-[0.3em] text-[#00FF9D] uppercase mb-6">/// Mehr entdecken ///</p>
          <h2 className="font-heading text-3xl md:text-5xl font-bold mb-8 text-white uppercase tracking-tight">
            Deine Stadt dabei?
          </h2>
          <p className="font-body text-lg text-slate-400 mb-12">
            Weitere Vorwahlen kommen bald. Schreib uns deine Wunsch-Stadt!
          </p>
          <Link to="/vorwahlen">
            <Button className="btn-outline px-12">Alle Vorwahlen</Button>
          </Link>
        </div>
      </section>
    </main>
  );
};

// ============= OLD SHOP PAGE (BACKUP) =============
const OldShop = ({ addToCart }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [vorwahlen, setVorwahlen] = useState([]);

  const category = searchParams.get("category") || "";
  const region = searchParams.get("region") || "";
  const vorwahl = searchParams.get("vorwahl") || "";
  const search = searchParams.get("search") || "";

  useEffect(() => { 
    fetchProducts(); 
    fetchVorwahlen();
  }, [category, region, vorwahl, search]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchVorwahlen = async () => {
    try {
      const res = await axios.get(`${API}/products/vorwahlen`);
      setVorwahlen(res.data.vorwahlen || []);
    } catch (error) { console.error(error); }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = {};
      if (category) params.category = category;
      if (region) params.region = region;
      if (vorwahl) params.vorwahl = vorwahl;
      if (search) params.search = search;
      const res = await axios.get(`${API}/products`, { params });
      setProducts(res.data.products);
      setTotal(res.data.total);
    } catch (error) { console.error(error); toast.error("Fehler beim Laden"); }
    setLoading(false);
  };

  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) newParams.set(key, value);
    else newParams.delete(key);
    setSearchParams(newParams);
  };

  const clearFilters = () => setSearchParams({});

  // Find stadt name for vorwahl
  const getVorwahlTitle = () => {
    if (vorwahl) {
      const stadtMap = {
        // 1. Bundesliga
        "089": "München", "0231": "Dortmund", "0214": "Leverkusen", "069": "Frankfurt",
        "0341": "Leipzig", "0711": "Stuttgart", "0761": "Freiburg", "06131": "Mainz",
        "02161": "Mönchengladbach", "05361": "Wolfsburg", "0821": "Augsburg", 
        "07261": "Sinsheim", "0421": "Bremen", "030": "Berlin", "0221": "Köln",
        "040": "Hamburg", "07321": "Heidenheim", "0521": "Bielefeld", "0211": "Düsseldorf",
        // 2. Bundesliga
        "0209": "Gelsenkirchen", "0511": "Hannover", "0911": "Nürnberg/Fürth",
        "0721": "Karlsruhe", "0431": "Kiel", "05251": "Paderborn", "06151": "Darmstadt",
        "0391": "Magdeburg", "06821": "Spiesen-Elversberg", "0251": "Münster",
        "0531": "Braunschweig", "0631": "Kaiserslautern", "0234": "Bochum", "0351": "Dresden",
        // 3. Liga
        "0201": "Essen", "0241": "Aachen", "0381": "Rostock", "0681": "Saarbrücken",
        "0355": "Cottbus", "0841": "Ingolstadt", "0621": "Mannheim", "05246": "Verl",
        "0541": "Osnabrück", "0731": "Ulm", "0941": "Regensburg", "0611": "Wiesbaden",
        "03771": "Aue-Bad Schlema", "0203": "Duisburg", "05131": "Garbsen", "09721": "Schweinfurt",
        // Regionalliga
        "0208": "Oberhausen", "0202": "Wuppertal", "0361": "Erfurt", "03641": "Jena",
        "0371": "Chemnitz", "0345": "Halle (Saale)", "0931": "Würzburg", "05931": "Meppen",
        "0441": "Oldenburg", "0651": "Trier", "05241": "Gütersloh"
      };
      return `${vorwahl} ${stadtMap[vorwahl] || ""}`;
    }
    return null;
  };

  const getCategoryTitle = () => {
    const categoryMap = {
      "t-shirts": "T-Shirts",
      "hoodies": "Hoodies",
      "kopfbedeckungen": "Kopfbedeckungen",
      "handyhuellen": "Handyhüllen",
      "tassen": "Tassen",
      "tragetaschen": "Tragetaschen"
    };
    return categoryMap[category] || category?.replace("-", " ");
  };

  return (
    <main className="min-h-screen" data-testid="shop-page">
      <section className="py-12 md:py-16 px-4 md:px-8 border-b border-black">
        <div className="max-w-7xl mx-auto">
          <h1 className="font-heading text-4xl md:text-6xl uppercase">
            {getVorwahlTitle() || (search ? `"${search}"` : category ? getCategoryTitle() : "Alle Produkte")}
          </h1>
          <p className="font-mono text-sm text-gray-500 mt-2 uppercase">{total} Produkte</p>
        </div>
      </section>

      <section className="py-6 px-4 md:px-8 border-b border-gray-200 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto flex flex-wrap gap-4 items-center">
          <Select value={vorwahl} onValueChange={(v) => updateFilter("vorwahl", v)}>
            <SelectTrigger className="w-[160px] sharp"><SelectValue placeholder="Vorwahl" /></SelectTrigger>
            <SelectContent className="sharp max-h-[300px]">
              {vorwahlen.sort().map(vw => (
                <SelectItem key={vw} value={vw}>{vw}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={category} onValueChange={(v) => updateFilter("category", v)}>
            <SelectTrigger className="w-[160px] sharp"><SelectValue placeholder="Kategorie" /></SelectTrigger>
            <SelectContent className="sharp">
              <SelectItem value="t-shirts">T-Shirts</SelectItem>
              <SelectItem value="hoodies">Hoodies</SelectItem>
              <SelectItem value="kopfbedeckungen">Kopfbedeckungen</SelectItem>
              <SelectItem value="handyhuellen">Handyhüllen</SelectItem>
              <SelectItem value="tassen">Tassen</SelectItem>
              <SelectItem value="tragetaschen">Tragetaschen</SelectItem>
            </SelectContent>
          </Select>

          <Select value={region} onValueChange={(v) => updateFilter("region", v)}>
            <SelectTrigger className="w-[140px] sharp"><SelectValue placeholder="Region" /></SelectTrigger>
            <SelectContent className="sharp">
              <SelectItem value="nrw">NRW</SelectItem>
              <SelectItem value="bayern">Bayern</SelectItem>
              <SelectItem value="nord">Nord</SelectItem>
              <SelectItem value="ost">Ost</SelectItem>
              <SelectItem value="suedwest">Südwest</SelectItem>
              <SelectItem value="berlin">Berlin</SelectItem>
            </SelectContent>
          </Select>

          {(category || region || search || vorwahl) && <Button variant="ghost" onClick={clearFilters} className="font-mono text-xs uppercase">Filter zurücksetzen</Button>}
        </div>
      </section>

      <section className="py-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-8">
              {[...Array(8)].map((_, i) => <div key={i} className="animate-pulse"><div className="aspect-square bg-gray-200"></div><div className="h-4 bg-gray-200 mt-4 w-1/3"></div><div className="h-4 bg-gray-200 mt-2 w-2/3"></div></div>)}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-16">
              <p className="font-body text-gray-500">Keine Produkte gefunden</p>
              <Button onClick={clearFilters} className="mt-4 sharp btn-secondary">Alle Produkte anzeigen</Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-8">
              {products.map((product) => <ProductCard key={product.id} product={product} addToCart={addToCart} />)}
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

// ============= PRODUCT DETAIL =============
const ProductDetail = ({ addToCart }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [quantity, setQuantity] = useState(1);

  useEffect(() => { fetchProduct(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/products/${id}`);
      setProduct(res.data);
      if (res.data.variants?.length > 0) {
        setSelectedSize(res.data.variants[0].size);
        setSelectedColor(res.data.variants[0].color);
      }
    } catch (error) { toast.error("Produkt nicht gefunden"); navigate("/shop"); }
    setLoading(false);
  };

  const getSelectedVariant = () => product?.variants?.find(v => v.size === selectedSize && v.color === selectedColor);

  const handleAddToCart = async () => {
    const variant = getSelectedVariant();
    if (!variant) { toast.error("Bitte Größe und Farbe wählen"); return; }
    await addToCart(product, variant, quantity);
    toast.success("Zum Warenkorb hinzugefügt!");
  };

  const sizes = [...new Set(product?.variants?.map(v => v.size) || [])];
  const colors = [...new Set(product?.variants?.map(v => v.color) || [])];
  const variant = getSelectedVariant();

  if (loading) return <div className="min-h-screen flex items-center justify-center"><p className="font-mono text-sm">Laden...</p></div>;
  if (!product) return null;

  return (
    <main className="min-h-screen py-8 md:py-16 px-4 md:px-8" data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16">
          <div className="aspect-square bg-gray-100 relative">
            <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
            {product.vorwahl === "0521" && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <img src="/logo-0521.png" alt="I Love 0521" className="w-3/4 h-3/4 object-contain" />
              </div>
            )}
          </div>
          <div className="py-4">
            <p className="font-mono text-sm text-gray-500 uppercase tracking-widest">{product.vorwahl} / {product.stadt}</p>
            <h1 className="font-heading text-4xl md:text-5xl uppercase mt-2">{product.name}</h1>
            <p className="font-mono text-2xl mt-4">€{variant?.price?.toFixed(2) || "—"}</p>
            <p className="font-body text-gray-600 mt-6 leading-relaxed">{product.description}</p>
            <Separator className="my-8" />

            <div className="mb-6">
              <Label className="font-body font-semibold uppercase tracking-widest text-sm mb-3 block">Größe</Label>
              <div className="flex flex-wrap gap-2">
                {sizes.map(size => (
                  <button key={size} onClick={() => setSelectedSize(size)} className={`px-4 py-2 border-2 font-mono text-sm transition-colors ${selectedSize === size ? "border-black bg-black text-white" : "border-gray-200 hover:border-black"}`}>{size}</button>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <Label className="font-body font-semibold uppercase tracking-widest text-sm mb-3 block">Farbe</Label>
              <div className="flex flex-wrap gap-2">
                {colors.map(color => (
                  <button key={color} onClick={() => setSelectedColor(color)} className={`px-4 py-2 border-2 font-mono text-sm transition-colors ${selectedColor === color ? "border-black bg-black text-white" : "border-gray-200 hover:border-black"}`}>{color}</button>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <Label className="font-body font-semibold uppercase tracking-widest text-sm mb-3 block">Menge</Label>
              <div className="flex items-center gap-4">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-10 h-10 border-2 border-black flex items-center justify-center hover:bg-black hover:text-white transition-colors"><Minus size={16} /></button>
                <span className="font-mono text-xl w-8 text-center">{quantity}</span>
                <button onClick={() => setQuantity(quantity + 1)} className="w-10 h-10 border-2 border-black flex items-center justify-center hover:bg-black hover:text-white transition-colors"><Plus size={16} /></button>
              </div>
            </div>

            {product.spreadshop_url ? (
              <a href={product.spreadshop_url} target="_blank" rel="noopener noreferrer" className="block">
                <Button className="w-full sharp btn-primary py-6 text-base" data-testid="buy-at-spreadshirt-btn">
                  Bei Spreadshirt kaufen – €{variant?.price?.toFixed(2) || "—"}
                </Button>
              </a>
            ) : (
              <Button onClick={handleAddToCart} className="w-full sharp btn-primary py-6 text-base" disabled={!variant} data-testid="add-to-cart-btn">
                In den Warenkorb – €{((variant?.price || 0) * quantity).toFixed(2)}
              </Button>
            )}

            <div className="mt-8 space-y-3">
              <div className="flex items-center gap-3 text-sm text-gray-500"><Truck size={16} /><span>Kostenloser Versand ab €50</span></div>
              <div className="flex items-center gap-3 text-sm text-gray-500"><Package size={16} /><span>On Demand produziert in 2-5 Werktagen</span></div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

// ============= CHECKOUT =============
const Checkout = ({ cart }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ first_name: "", last_name: "", email: "", street: "", city: "", zip_code: "", country: "DE" });

  const subtotal = cart?.items?.reduce((sum, item) => sum + item.price * item.quantity, 0) || 0;
  const shipping = subtotal >= 50 ? 0 : 4.99;
  const total = subtotal + shipping;

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!cart?.items?.length) { toast.error("Warenkorb ist leer"); return; }
    setLoading(true);
    try {
      const orderData = {
        items: cart.items.map(item => ({ product_id: item.product_id, variant_id: item.variant_id, quantity: item.quantity, name: item.name, vorwahl: item.vorwahl, size: item.size, color: item.color, price: item.price })),
        shipping: formData
      };
      const res = await axios.post(`${API}/orders`, orderData);
      await axios.delete(`${API}/cart/${getSessionId()}/clear`);
      window.dispatchEvent(new Event("cartUpdated"));
      toast.success("Bestellung erfolgreich!");
      navigate(`/order-confirmation/${res.data.order.id}`);
    } catch (error) { toast.error("Fehler bei der Bestellung"); }
    setLoading(false);
  };

  if (!cart?.items?.length) {
    return <main className="min-h-screen flex items-center justify-center px-4"><div className="text-center"><h1 className="font-heading text-4xl uppercase mb-4">Warenkorb leer</h1><Button onClick={() => navigate("/shop")} className="sharp btn-primary">Zum Shop</Button></div></main>;
  }

  return (
    <main className="min-h-screen py-8 md:py-16 px-4 md:px-8" data-testid="checkout-page">
      <div className="max-w-6xl mx-auto">
        <h1 className="font-heading text-4xl md:text-5xl uppercase mb-12">Kasse</h1>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <h2 className="font-heading text-2xl uppercase mb-6">Lieferadresse</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="font-mono text-xs uppercase">Vorname</Label><Input name="first_name" value={formData.first_name} onChange={handleChange} className="sharp mt-1" required /></div>
                <div><Label className="font-mono text-xs uppercase">Nachname</Label><Input name="last_name" value={formData.last_name} onChange={handleChange} className="sharp mt-1" required /></div>
              </div>
              <div><Label className="font-mono text-xs uppercase">Email</Label><Input name="email" type="email" value={formData.email} onChange={handleChange} className="sharp mt-1" required /></div>
              <div><Label className="font-mono text-xs uppercase">Straße & Hausnummer</Label><Input name="street" value={formData.street} onChange={handleChange} className="sharp mt-1" required /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="font-mono text-xs uppercase">PLZ</Label><Input name="zip_code" value={formData.zip_code} onChange={handleChange} className="sharp mt-1" required /></div>
                <div><Label className="font-mono text-xs uppercase">Stadt</Label><Input name="city" value={formData.city} onChange={handleChange} className="sharp mt-1" required /></div>
              </div>
              <Separator className="my-8" />
              <h2 className="font-heading text-2xl uppercase mb-4">Zahlung</h2>
              <p className="font-body text-gray-500 text-sm mb-4">PayPal-Integration wird nach Konfiguration aktiviert.</p>
              <Button type="submit" className="w-full sharp btn-primary py-6 text-base" disabled={loading}>{loading ? "Verarbeite..." : `Jetzt bestellen – €${total.toFixed(2)}`}</Button>
            </form>
          </div>
          <div className="bg-[#FAFAFA] p-6 md:p-8 h-fit">
            <h2 className="font-heading text-2xl uppercase mb-6">Bestellübersicht</h2>
            <div className="space-y-4">
              {cart.items.map(item => (
                <div key={`${item.product_id}-${item.variant_id}`} className="flex gap-4">
                  <div className="w-16 h-16 bg-gray-200 flex-shrink-0"><img src={item.image_url} alt={item.name} className="w-full h-full object-cover" /></div>
                  <div className="flex-1"><p className="font-body font-semibold text-sm">{item.name}</p><p className="font-mono text-xs text-gray-500">{item.size} / {item.color} × {item.quantity}</p></div>
                  <p className="font-mono text-sm">€{(item.price * item.quantity).toFixed(2)}</p>
                </div>
              ))}
            </div>
            <Separator className="my-6" />
            <div className="space-y-2">
              <div className="flex justify-between font-body text-sm"><span>Zwischensumme</span><span className="font-mono">€{subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between font-body text-sm"><span>Versand</span><span className="font-mono">{shipping === 0 ? "KOSTENLOS" : `€${shipping.toFixed(2)}`}</span></div>
              <Separator className="my-4" />
              <div className="flex justify-between font-body font-bold text-lg"><span>Gesamt</span><span className="font-mono">€{total.toFixed(2)}</span></div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

// ============= ORDER CONFIRMATION =============
const OrderConfirmation = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchOrder(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchOrder = async () => {
    try {
      const res = await axios.get(`${API}/orders/${id}`);
      setOrder(res.data);
    } catch (error) { toast.error("Bestellung nicht gefunden"); navigate("/"); }
    setLoading(false);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><p className="font-mono">Laden...</p></div>;
  if (!order) return null;

  return (
    <main className="min-h-screen py-16 px-4 md:px-8" data-testid="order-confirmation-page">
      <div className="max-w-2xl mx-auto text-center">
        <div className="w-20 h-20 bg-green-500 text-white flex items-center justify-center mx-auto mb-6"><CheckCircle size={40} /></div>
        <h1 className="font-heading text-4xl md:text-5xl uppercase mb-4">Danke!</h1>
        <p className="font-body text-gray-600 mb-8">Deine Bestellung wurde erfolgreich aufgegeben.</p>
        <div className="bg-[#FAFAFA] p-6 text-left mb-8">
          <p className="font-mono text-sm text-gray-500 uppercase">Bestellnummer</p>
          <p className="font-heading text-2xl">{order.order_number}</p>
          <Separator className="my-4" />
          <div className="space-y-2">
            <p className="font-body text-sm"><strong>Status:</strong> {order.status}</p>
            <p className="font-body text-sm"><strong>Gesamt:</strong> €{order.total?.toFixed(2)}</p>
            <p className="font-body text-sm"><strong>Lieferung an:</strong> {order.shipping?.first_name} {order.shipping?.last_name}</p>
            <p className="font-body text-sm">{order.shipping?.street}, {order.shipping?.zip_code} {order.shipping?.city}</p>
          </div>
        </div>
        <Button onClick={() => navigate("/shop")} className="sharp btn-primary">Weiter einkaufen</Button>
      </div>
    </main>
  );
};

// ============= ABOUT =============
const About = () => (
  <main className="min-h-screen py-16 px-4 md:px-8" data-testid="about-page">
    <div className="max-w-4xl mx-auto">
      <h1 className="font-heading text-5xl md:text-7xl uppercase mb-8">Über VorwahlLiebe</h1>
      <div className="space-y-6 font-body text-lg leading-relaxed text-gray-700">
        <p><strong className="text-black">VorwahlLiebe</strong> ist mehr als nur eine Marke – es ist eine Bewegung für alle, die stolz auf ihre Herkunft sind.</p>
        <p>Unsere Mission ist einfach: Wir bringen lokale Identität auf die Straße. Mit minimalistischen Designs, die deine Vorwahl feiern, ohne dabei laut zu sein. Clean. Urban. Echt.</p>
        <p>Jedes Stück wird <strong className="text-black">on demand</strong> in Europa produziert – kein Lager, kein Überschuss, nur hochwertige Textilien mit erstklassigem Druck.</p>
      </div>
      <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="border-l-4 border-black pl-4"><h3 className="font-heading text-xl uppercase mb-2">Minimalistisch</h3><p className="font-body text-gray-600">Klare Designs, keine Spielereien</p></div>
        <div className="border-l-4 border-black pl-4"><h3 className="font-heading text-xl uppercase mb-2">Lokal</h3><p className="font-body text-gray-600">Zeig, woher du kommst</p></div>
        <div className="border-l-4 border-black pl-4"><h3 className="font-heading text-xl uppercase mb-2">Nachhaltig</h3><p className="font-body text-gray-600">On Demand, EU-Produktion</p></div>
      </div>
    </div>
  </main>
);

// ============= VORWAHLEN PAGE =============
const VorwahlenPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("search")?.toLowerCase() || "";
  const [localSearch, setLocalSearch] = useState(searchQuery);
  
  const bundesligaStaedte = [
    { vorwahl: "089", stadt: "München", region: "bayern" },
    { vorwahl: "0231", stadt: "Dortmund", region: "nrw" },
    { vorwahl: "0214", stadt: "Leverkusen", region: "nrw" },
    { vorwahl: "069", stadt: "Frankfurt am Main", region: "suedwest" },
    { vorwahl: "0341", stadt: "Leipzig", region: "ost" },
    { vorwahl: "0711", stadt: "Stuttgart", region: "suedwest" },
    { vorwahl: "0761", stadt: "Freiburg im Breisgau", region: "suedwest" },
    { vorwahl: "06131", stadt: "Mainz", region: "suedwest" },
    { vorwahl: "02161", stadt: "Mönchengladbach", region: "nrw" },
    { vorwahl: "05361", stadt: "Wolfsburg", region: "nord" },
    { vorwahl: "0821", stadt: "Augsburg", region: "bayern" },
    { vorwahl: "07261", stadt: "Sinsheim", region: "suedwest" },
    { vorwahl: "0421", stadt: "Bremen", region: "nord" },
    { vorwahl: "030", stadt: "Berlin", region: "berlin" },
    { vorwahl: "0221", stadt: "Köln", region: "nrw" },
    { vorwahl: "040", stadt: "Hamburg", region: "nord" },
    { vorwahl: "07321", stadt: "Heidenheim", region: "suedwest" },
  ];

  const zweiteLigaStaedte = [
    { vorwahl: "0209", stadt: "Gelsenkirchen", region: "nrw" },
    { vorwahl: "0511", stadt: "Hannover", region: "nord" },
    { vorwahl: "0911", stadt: "Nürnberg", region: "bayern" },
    { vorwahl: "0721", stadt: "Karlsruhe", region: "suedwest" },
    { vorwahl: "0431", stadt: "Kiel", region: "nord" },
    { vorwahl: "05251", stadt: "Paderborn", region: "nrw" },
    { vorwahl: "06151", stadt: "Darmstadt", region: "suedwest" },
    { vorwahl: "0391", stadt: "Magdeburg", region: "ost" },
    { vorwahl: "06821", stadt: "Spiesen-Elversberg", region: "suedwest" },
    { vorwahl: "0251", stadt: "Münster", region: "nrw" },
    { vorwahl: "0531", stadt: "Braunschweig", region: "nord" },
    { vorwahl: "0631", stadt: "Kaiserslautern", region: "suedwest" },
    { vorwahl: "0234", stadt: "Bochum", region: "nrw" },
    { vorwahl: "0351", stadt: "Dresden", region: "ost" },
  ];

  const dritteLigaStaedte = [
    { vorwahl: "0201", stadt: "Essen", region: "nrw" },
    { vorwahl: "0241", stadt: "Aachen", region: "nrw" },
    { vorwahl: "0381", stadt: "Rostock", region: "nord" },
    { vorwahl: "0681", stadt: "Saarbrücken", region: "suedwest" },
    { vorwahl: "0355", stadt: "Cottbus", region: "ost" },
    { vorwahl: "0841", stadt: "Ingolstadt", region: "bayern" },
    { vorwahl: "0621", stadt: "Mannheim", region: "suedwest" },
    { vorwahl: "05246", stadt: "Verl", region: "nrw" },
    { vorwahl: "0541", stadt: "Osnabrück", region: "nord" },
    { vorwahl: "0731", stadt: "Ulm", region: "suedwest" },
    { vorwahl: "0941", stadt: "Regensburg", region: "bayern" },
    { vorwahl: "0611", stadt: "Wiesbaden", region: "suedwest" },
    { vorwahl: "03771", stadt: "Aue-Bad Schlema", region: "ost" },
    { vorwahl: "0203", stadt: "Duisburg", region: "nrw" },
    { vorwahl: "05131", stadt: "Garbsen", region: "nord" },
    { vorwahl: "09721", stadt: "Schweinfurt", region: "bayern" },
  ];

  const regionalligaStaedte = [
    { vorwahl: "0208", stadt: "Oberhausen", region: "nrw" },
    { vorwahl: "0202", stadt: "Wuppertal", region: "nrw" },
    { vorwahl: "0361", stadt: "Erfurt", region: "ost" },
    { vorwahl: "03641", stadt: "Jena", region: "ost" },
    { vorwahl: "0371", stadt: "Chemnitz", region: "ost" },
    { vorwahl: "0345", stadt: "Halle (Saale)", region: "ost" },
    { vorwahl: "0931", stadt: "Würzburg", region: "bayern" },
    { vorwahl: "05931", stadt: "Meppen", region: "nord" },
    { vorwahl: "0441", stadt: "Oldenburg", region: "nord" },
    { vorwahl: "0651", stadt: "Trier", region: "suedwest" },
    { vorwahl: "05241", stadt: "Gütersloh", region: "nrw" },
    { vorwahl: "0241", stadt: "Aachen", region: "nrw" },
    { vorwahl: "0341", stadt: "Leipzig", region: "ost" },
    { vorwahl: "069", stadt: "Offenbach", region: "suedwest" },
    { vorwahl: "0711", stadt: "Stuttgart", region: "suedwest" },
    { vorwahl: "0221", stadt: "Köln", region: "nrw" },
  ];

  // Combine all cities for search
  const allStaedte = [...bundesligaStaedte, ...zweiteLigaStaedte, ...dritteLigaStaedte, ...regionalligaStaedte];
  
  // Filter function
  const filterBySearch = (items) => {
    if (!localSearch) return items;
    return items.filter(item => 
      item.vorwahl.includes(localSearch) || 
      item.stadt.toLowerCase().includes(localSearch.toLowerCase())
    );
  };

  // Get filtered results
  const filteredBundesliga = filterBySearch(bundesligaStaedte);
  const filteredZweiteLiga = filterBySearch(zweiteLigaStaedte);
  const filteredDritteLiga = filterBySearch(dritteLigaStaedte);
  const filteredRegionalliga = filterBySearch(regionalligaStaedte);

  // Check if there are any results
  const hasResults = filteredBundesliga.length > 0 || filteredZweiteLiga.length > 0 || 
                     filteredDritteLiga.length > 0 || filteredRegionalliga.length > 0;

  const VorwahlCard = ({ item }) => (
    <div 
      onClick={() => navigate(`/shop?vorwahl=${item.vorwahl}`)}
      className="grid-card group cursor-pointer"
      data-testid={`vorwahl-card-${item.vorwahl}`}
    >
      <p className="vorwahl-number">{item.vorwahl}</p>
      <p className="font-body text-gray-500 mt-1 text-sm">{item.stadt}</p>
    </div>
  );

  return (
    <main className="min-h-screen py-20 px-6 md:px-8 bg-[#0A0A0A]" data-testid="vorwahlen-page">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-16">
          <p className="font-mono text-xs tracking-[0.3em] text-gray-600 uppercase mb-6">Kollektion</p>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-medium mb-6 text-white">Alle Vorwahlen</h1>
          <p className="font-body text-lg text-gray-500 mb-8">Finde deine Stadt</p>
          
          {/* Search Input */}
          <div className="max-w-md mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-500" size={20} />
              <Input
                type="text"
                placeholder="Vorwahl oder Stadt suchen..."
                value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
                className="pl-12 py-4 bg-[#050505] border-white/10 text-white font-mono text-lg"
                data-testid="vorwahl-search-input"
              />
              {localSearch && (
                <button 
                  onClick={() => setLocalSearch("")}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-white"
                >
                  <X size={20} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Show search results or all categories */}
        {!hasResults && localSearch ? (
          <div className="text-center py-20">
            <p className="font-mono text-slate-500 mb-4">Keine Vorwahl gefunden für "{localSearch}"</p>
            <Button onClick={() => setLocalSearch("")} className="btn-outline">
              Alle anzeigen
            </Button>
          </div>
        ) : (
          <>
            {filteredBundesliga.length > 0 && (
              <div className="mb-20">
                <h2 className="font-mono text-xs tracking-[0.2em] text-gray-600 uppercase mb-8 pb-4 border-b border-white/5">Bundesliga</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {filteredBundesliga.map((item) => <VorwahlCard key={item.vorwahl} item={item} />)}
                </div>
              </div>
            )}

            {filteredZweiteLiga.length > 0 && (
              <div className="mb-20">
                <h2 className="font-mono text-xs tracking-[0.2em] text-gray-600 uppercase mb-8 pb-4 border-b border-white/5">2. Bundesliga</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {filteredZweiteLiga.map((item) => <VorwahlCard key={`2liga-${item.vorwahl}`} item={item} />)}
                </div>
              </div>
            )}

            {filteredDritteLiga.length > 0 && (
              <div className="mb-20">
                <h2 className="font-mono text-xs tracking-[0.2em] text-gray-600 uppercase mb-8 pb-4 border-b border-white/5">3. Liga</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {filteredDritteLiga.map((item) => <VorwahlCard key={`3liga-${item.vorwahl}`} item={item} />)}
                </div>
              </div>
            )}

            {filteredRegionalliga.length > 0 && (
              <div className="mb-20">
                <h2 className="font-mono text-xs tracking-[0.2em] text-gray-600 uppercase mb-8 pb-4 border-b border-white/5">Weitere</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {filteredRegionalliga.map((item) => <VorwahlCard key={`4liga-${item.vorwahl}`} item={item} />)}
                </div>
              </div>
            )}
          </>
        )}

        <div className="bg-[#141414] p-12 text-center border border-white/5">
          <h3 className="font-heading text-xl font-medium mb-4 text-white">Deine Vorwahl fehlt?</h3>
          <p className="font-body text-gray-500 mb-8">Wir erweitern ständig.</p>
          <a href="mailto:info@vorwahlliebe.de">
            <Button className="btn-outline">Kontakt</Button>
          </a>
        </div>
      </div>
    </main>
  );
};

// ============= LEGAL PAGES =============
const LegalPage = ({ title, children }) => (
  <main className="min-h-screen py-16 px-4 md:px-8" data-testid="legal-page">
    <div className="max-w-4xl mx-auto">
      <h1 className="font-heading text-4xl md:text-5xl uppercase mb-8 border-b-4 border-black pb-4">{title}</h1>
      <div className="prose prose-lg max-w-none font-body text-gray-700 space-y-6">
        {children}
      </div>
    </div>
  </main>
);

const Impressum = () => (
  <LegalPage title="Impressum">
    <section>
      <h2 className="font-heading text-2xl uppercase mt-8 mb-4">Angaben gemäß § 5 TMG</h2>
      <p>
        <strong>Interpohl Solutions GmbH i. Gr.</strong><br />
        Kontor H72<br />
        Hansastr. 72<br />
        44137 Dortmund<br />
        Deutschland
      </p>
    </section>

    <section>
      <h2 className="font-heading text-2xl uppercase mt-8 mb-4">Kontakt</h2>
      <p>
        E-Mail: info@vorwahlliebe.de<br />
        Telefon: 0170-7967959
      </p>
    </section>

    <section>
      <h2 className="font-heading text-2xl uppercase mt-8 mb-4">Vertretungsberechtigter Geschäftsführer</h2>
      <p>Tobias Pohl</p>
    </section>

    <section>
      <h2 className="font-heading text-2xl uppercase mt-8 mb-4">Registereintrag</h2>
      <p>
        Eintragung im Handelsregister.<br />
        Registergericht: Amtsgericht Dortmund<br />
        Registernummer: [HRB-Nummer nach Eintragung einfügen]<br />
        <em>Hinweis: Die Gesellschaft befindet sich in Gründung (i. Gr.)</em>
      </p>
    </section>

    <section>
      <h2 className="font-heading text-2xl uppercase mt-8 mb-4">Umsatzsteuer-ID</h2>
      <p>
        Umsatzsteuer-Identifikationsnummer gemäß § 27a Umsatzsteuergesetz:<br />
        [USt-IdNr. nach Erteilung einfügen]
      </p>
    </section>

    <section>
      <h2 className="font-heading text-2xl uppercase mt-8 mb-4">Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV</h2>
      <p>
        Tobias Pohl<br />
        Hansastr. 72<br />
        44137 Dortmund
      </p>
    </section>

    <section>
      <h2 className="font-heading text-2xl uppercase mt-8 mb-4">EU-Streitschlichtung</h2>
      <p>
        Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:{" "}
        <a href="https://ec.europa.eu/consumers/odr/" target="_blank" rel="noopener noreferrer" className="text-[#FF4500] hover:underline">
          https://ec.europa.eu/consumers/odr/
        </a>
      </p>
      <p>Unsere E-Mail-Adresse finden Sie oben im Impressum.</p>
    </section>

    <section>
      <h2 className="font-heading text-2xl uppercase mt-8 mb-4">Verbraucherstreitbeilegung/Universalschlichtungsstelle</h2>
      <p>
        Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer 
        Verbraucherschlichtungsstelle teilzunehmen.
      </p>
    </section>

    <section>
      <h2 className="font-heading text-2xl uppercase mt-8 mb-4">Haftung für Inhalte</h2>
      <p>
        Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten nach den 
        allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht 
        verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen 
        zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen.
      </p>
      <p>
        Verpflichtungen zur Entfernung oder Sperrung der Nutzung von Informationen nach den allgemeinen 
        Gesetzen bleiben hiervon unberührt. Eine diesbezügliche Haftung ist jedoch erst ab dem Zeitpunkt 
        der Kenntnis einer konkreten Rechtsverletzung möglich. Bei Bekanntwerden von entsprechenden 
        Rechtsverletzungen werden wir diese Inhalte umgehend entfernen.
      </p>
    </section>

    <section>
      <h2 className="font-heading text-2xl uppercase mt-8 mb-4">Haftung für Links</h2>
      <p>
        Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. 
        Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der 
        verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich.
      </p>
    </section>

    <section>
      <h2 className="font-heading text-2xl uppercase mt-8 mb-4">Urheberrecht</h2>
      <p>
        Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen 
        Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der 
        Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers.
      </p>
    </section>
  </LegalPage>
);

const Datenschutz = () => (
  <LegalPage title="Datenschutzerklärung">
    <section>
      <h2 className="font-heading text-2xl uppercase mt-8 mb-4">1. Datenschutz auf einen Blick</h2>
      <h3 className="font-heading text-xl mt-6 mb-3">Allgemeine Hinweise</h3>
      <p>
        Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten 
        passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie 
        persönlich identifiziert werden können.
      </p>
      
      <h3 className="font-heading text-xl mt-6 mb-3">Datenerfassung auf dieser Website</h3>
      <p><strong>Wer ist verantwortlich für die Datenerfassung auf dieser Website?</strong></p>
      <p>
        Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen Kontaktdaten 
        können Sie dem Impressum dieser Website entnehmen.
      </p>
      
      <p className="mt-4"><strong>Wie erfassen wir Ihre Daten?</strong></p>
      <p>
        Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. Hierbei kann es sich z.B. 
        um Daten handeln, die Sie in ein Kontaktformular eingeben oder bei einer Bestellung angeben.
      </p>
      <p>
        Andere Daten werden automatisch oder nach Ihrer Einwilligung beim Besuch der Website durch unsere 
        IT-Systeme erfasst. Das sind vor allem technische Daten (z.B. Internetbrowser, Betriebssystem oder 
        Uhrzeit des Seitenaufrufs).
      </p>
      
      <p className="mt-4"><strong>Wofür nutzen wir Ihre Daten?</strong></p>
      <p>
        Ein Teil der Daten wird erhoben, um eine fehlerfreie Bereitstellung der Website zu gewährleisten. 
        Andere Daten können zur Analyse Ihres Nutzerverhaltens verwendet werden. Weitere Daten dienen der 
        Abwicklung von Bestellungen und der Kommunikation mit Ihnen.
      </p>
      
      <p className="mt-4"><strong>Welche Rechte haben Sie bezüglich Ihrer Daten?</strong></p>
      <p>
        Sie haben jederzeit das Recht, unentgeltlich Auskunft über Herkunft, Empfänger und Zweck Ihrer 
        gespeicherten personenbezogenen Daten zu erhalten. Sie haben außerdem ein Recht, die Berichtigung 
        oder Löschung dieser Daten zu verlangen. Wenn Sie eine Einwilligung zur Datenverarbeitung erteilt 
        haben, können Sie diese Einwilligung jederzeit für die Zukunft widerrufen.
      </p>
    </section>

    <section>
      <h2 className="font-heading text-2xl uppercase mt-8 mb-4">2. Verantwortliche Stelle</h2>
      <p>
        <strong>Interpohl Solutions GmbH i. Gr.</strong><br />
        Geschäftsführer: Tobias Pohl<br />
        Kontor H72<br />
        Hansastr. 72<br />
        44137 Dortmund<br />
        Deutschland<br /><br />
        Telefon: 0170-7967959<br />
        E-Mail: info@vorwahlliebe.de
      </p>
    </section>

    <section>
      <h2 className="font-heading text-2xl uppercase mt-8 mb-4">3. Datenerfassung auf dieser Website</h2>
      
      <h3 className="font-heading text-xl mt-6 mb-3">Cookies</h3>
      <p>
        Unsere Internetseiten verwenden so genannte „Cookies". Cookies sind kleine Datenpakete und richten 
        auf Ihrem Endgerät keinen Schaden an. Sie werden entweder vorübergehend für die Dauer einer Sitzung 
        (Session-Cookies) oder dauerhaft (permanente Cookies) auf Ihrem Endgerät gespeichert.
      </p>
      <p>
        Sie können Ihren Browser so einstellen, dass Sie über das Setzen von Cookies informiert werden und 
        Cookies nur im Einzelfall erlauben, die Annahme von Cookies für bestimmte Fälle oder generell 
        ausschließen sowie das automatische Löschen der Cookies beim Schließen des Browsers aktivieren.
      </p>
      
      <h3 className="font-heading text-xl mt-6 mb-3">Server-Log-Dateien</h3>
      <p>
        Der Provider der Seiten erhebt und speichert automatisch Informationen in so genannten Server-Log-Dateien, 
        die Ihr Browser automatisch an uns übermittelt. Dies sind:
      </p>
      <ul className="list-disc pl-6 mt-2">
        <li>Browsertyp und Browserversion</li>
        <li>verwendetes Betriebssystem</li>
        <li>Referrer URL</li>
        <li>Hostname des zugreifenden Rechners</li>
        <li>Uhrzeit der Serveranfrage</li>
        <li>IP-Adresse</li>
      </ul>
      <p className="mt-2">
        Eine Zusammenführung dieser Daten mit anderen Datenquellen wird nicht vorgenommen. Die Erfassung 
        dieser Daten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO.
      </p>
    </section>

    <section>
      <h2 className="font-heading text-2xl uppercase mt-8 mb-4">4. Bestellungen und Vertragsabwicklung</h2>
      <p>
        Wir erheben personenbezogene Daten, wenn Sie eine Bestellung in unserem Online-Shop aufgeben. 
        Erforderlich sind: Name, Adresse, E-Mail-Adresse und Zahlungsinformationen.
      </p>
      <p>
        Diese Daten werden zur Vertragserfüllung gemäß Art. 6 Abs. 1 lit. b DSGVO verarbeitet. Die Daten 
        werden gelöscht, sobald sie für die Erreichung des Zweckes ihrer Erhebung nicht mehr erforderlich 
        sind und keine gesetzlichen Aufbewahrungspflichten entgegenstehen.
      </p>
    </section>

    <section>
      <h2 className="font-heading text-2xl uppercase mt-8 mb-4">5. Zahlungsdienstleister</h2>
      <p>
        Für die Zahlungsabwicklung nutzen wir externe Zahlungsdienstleister. Ihre Zahlungsdaten werden 
        direkt an den jeweiligen Dienstleister übermittelt und dort verarbeitet. Die Datenschutzbestimmungen 
        des jeweiligen Anbieters finden Sie auf dessen Website.
      </p>
    </section>

    <section>
      <h2 className="font-heading text-2xl uppercase mt-8 mb-4">6. Ihre Rechte</h2>
      <p>Sie haben folgende Rechte:</p>
      <ul className="list-disc pl-6 mt-2">
        <li><strong>Auskunftsrecht (Art. 15 DSGVO):</strong> Sie können Auskunft über Ihre verarbeiteten Daten verlangen.</li>
        <li><strong>Berichtigungsrecht (Art. 16 DSGVO):</strong> Sie können die Berichtigung unrichtiger Daten verlangen.</li>
        <li><strong>Löschungsrecht (Art. 17 DSGVO):</strong> Sie können die Löschung Ihrer Daten verlangen.</li>
        <li><strong>Einschränkung der Verarbeitung (Art. 18 DSGVO):</strong> Sie können die Einschränkung der Verarbeitung verlangen.</li>
        <li><strong>Datenübertragbarkeit (Art. 20 DSGVO):</strong> Sie können Ihre Daten in einem gängigen Format erhalten.</li>
        <li><strong>Widerspruchsrecht (Art. 21 DSGVO):</strong> Sie können der Verarbeitung widersprechen.</li>
      </ul>
      <p className="mt-4">
        <strong>Beschwerderecht:</strong> Sie haben das Recht, sich bei einer Datenschutz-Aufsichtsbehörde zu beschweren.
      </p>
    </section>
  </LegalPage>
);

const Widerruf = () => (
  <LegalPage title="Widerrufsbelehrung">
    <section>
      <h2 className="font-heading text-2xl uppercase mt-8 mb-4">Widerrufsrecht für Verbraucher</h2>
      <p>
        Verbraucher ist jede natürliche Person, die ein Rechtsgeschäft zu Zwecken abschließt, die 
        überwiegend weder ihrer gewerblichen noch ihrer selbständigen beruflichen Tätigkeit zugerechnet 
        werden können.
      </p>
    </section>

    <section>
      <h2 className="font-heading text-2xl uppercase mt-8 mb-4">Widerrufsrecht</h2>
      <p>
        Sie haben das Recht, binnen vierzehn Tagen ohne Angabe von Gründen diesen Vertrag zu widerrufen.
      </p>
      <p>
        Die Widerrufsfrist beträgt vierzehn Tage ab dem Tag, an dem Sie oder ein von Ihnen benannter 
        Dritter, der nicht der Beförderer ist, die Waren in Besitz genommen haben bzw. hat.
      </p>
      <p>
        Um Ihr Widerrufsrecht auszuüben, müssen Sie uns
      </p>
      <p className="bg-gray-100 p-4 my-4">
        <strong>Interpohl Solutions GmbH i. Gr.</strong><br />
        Geschäftsführer: Tobias Pohl<br />
        Kontor H72<br />
        Hansastr. 72<br />
        44137 Dortmund<br />
        Telefon: 0170-7967959<br />
        E-Mail: info@vorwahlliebe.de
      </p>
      <p>
        mittels einer eindeutigen Erklärung (z.B. ein mit der Post versandter Brief oder E-Mail) über 
        Ihren Entschluss, diesen Vertrag zu widerrufen, informieren. Sie können dafür das beigefügte 
        Muster-Widerrufsformular verwenden, das jedoch nicht vorgeschrieben ist.
      </p>
      <p>
        Zur Wahrung der Widerrufsfrist reicht es aus, dass Sie die Mitteilung über die Ausübung des 
        Widerrufsrechts vor Ablauf der Widerrufsfrist absenden.
      </p>
    </section>

    <section>
      <h2 className="font-heading text-2xl uppercase mt-8 mb-4">Folgen des Widerrufs</h2>
      <p>
        Wenn Sie diesen Vertrag widerrufen, haben wir Ihnen alle Zahlungen, die wir von Ihnen erhalten 
        haben, einschließlich der Lieferkosten (mit Ausnahme der zusätzlichen Kosten, die sich daraus 
        ergeben, dass Sie eine andere Art der Lieferung als die von uns angebotene, günstigste 
        Standardlieferung gewählt haben), unverzüglich und spätestens binnen vierzehn Tagen ab dem Tag 
        zurückzuzahlen, an dem die Mitteilung über Ihren Widerruf dieses Vertrags bei uns eingegangen ist.
      </p>
      <p>
        Für diese Rückzahlung verwenden wir dasselbe Zahlungsmittel, das Sie bei der ursprünglichen 
        Transaktion eingesetzt haben, es sei denn, mit Ihnen wurde ausdrücklich etwas anderes vereinbart; 
        in keinem Fall werden Ihnen wegen dieser Rückzahlung Entgelte berechnet.
      </p>
      <p>
        Wir können die Rückzahlung verweigern, bis wir die Waren wieder zurückerhalten haben oder bis Sie 
        den Nachweis erbracht haben, dass Sie die Waren zurückgesandt haben, je nachdem, welches der frühere 
        Zeitpunkt ist.
      </p>
      <p>
        Sie haben die Waren unverzüglich und in jedem Fall spätestens binnen vierzehn Tagen ab dem Tag, an 
        dem Sie uns über den Widerruf dieses Vertrags unterrichten, an uns zurückzusenden oder zu übergeben. 
        Die Frist ist gewahrt, wenn Sie die Waren vor Ablauf der Frist von vierzehn Tagen absenden.
      </p>
      <p>
        <strong>Sie tragen die unmittelbaren Kosten der Rücksendung der Waren.</strong>
      </p>
      <p>
        Sie müssen für einen etwaigen Wertverlust der Waren nur aufkommen, wenn dieser Wertverlust auf einen 
        zur Prüfung der Beschaffenheit, Eigenschaften und Funktionsweise der Waren nicht notwendigen Umgang 
        mit ihnen zurückzuführen ist.
      </p>
    </section>

    <section>
      <h2 className="font-heading text-2xl uppercase mt-8 mb-4">Ausschluss des Widerrufsrechts</h2>
      <p>Das Widerrufsrecht besteht nicht bei Verträgen zur Lieferung von Waren, die nicht vorgefertigt 
      sind und für deren Herstellung eine individuelle Auswahl oder Bestimmung durch den Verbraucher 
      maßgeblich ist oder die eindeutig auf die persönlichen Bedürfnisse des Verbrauchers zugeschnitten sind.</p>
      <p className="mt-4 bg-yellow-50 border-l-4 border-yellow-400 p-4">
        <strong>Hinweis:</strong> Da unsere Produkte im Print-on-Demand-Verfahren individuell für Sie 
        hergestellt werden, kann das Widerrufsrecht bei diesen Produkten gemäß § 312g Abs. 2 Nr. 1 BGB 
        ausgeschlossen sein.
      </p>
    </section>

    <section>
      <h2 className="font-heading text-2xl uppercase mt-8 mb-4">Muster-Widerrufsformular</h2>
      <p>(Wenn Sie den Vertrag widerrufen wollen, dann füllen Sie bitte dieses Formular aus und senden Sie es zurück.)</p>
      <div className="bg-gray-100 p-4 my-4 border">
        <p>An:<br />
        Interpohl Solutions GmbH i. Gr.<br />
        Geschäftsführer: Tobias Pohl<br />
        Kontor H72<br />
        Hansastr. 72<br />
        44137 Dortmund<br />
        Telefon: 0170-7967959<br />
        E-Mail: info@vorwahlliebe.de</p>
        <p className="mt-4">Hiermit widerrufe(n) ich/wir (*) den von mir/uns (*) abgeschlossenen Vertrag über den Kauf der folgenden Waren (*):</p>
        <p className="mt-2">_______________________________________</p>
        <p className="mt-4">Bestellt am (*) / erhalten am (*):</p>
        <p className="mt-2">_______________________________________</p>
        <p className="mt-4">Name des/der Verbraucher(s):</p>
        <p className="mt-2">_______________________________________</p>
        <p className="mt-4">Anschrift des/der Verbraucher(s):</p>
        <p className="mt-2">_______________________________________</p>
        <p className="mt-4">_______________________________________</p>
        <p className="mt-4">Unterschrift des/der Verbraucher(s) (nur bei Mitteilung auf Papier):</p>
        <p className="mt-2">_______________________________________</p>
        <p className="mt-4">Datum:</p>
        <p className="mt-2">_______________________________________</p>
        <p className="mt-4 text-sm">(*) Unzutreffendes streichen.</p>
      </div>
    </section>
  </LegalPage>
);

const AGB = () => (
  <LegalPage title="Allgemeine Geschäftsbedingungen">
    <section>
      <h2 className="font-heading text-2xl uppercase mt-8 mb-4">§ 1 Geltungsbereich</h2>
      <p>
        (1) Diese Allgemeinen Geschäftsbedingungen (nachfolgend "AGB") der Interpohl Solutions GmbH i. Gr. 
        (nachfolgend "Verkäufer"), gelten für alle Verträge über die Lieferung von Waren, die ein 
        Verbraucher oder Unternehmer (nachfolgend "Kunde") mit dem Verkäufer über den Online-Shop des 
        Verkäufers unter www.vorwahlliebe.de abschließt.
      </p>
      <p>
        (2) Verbraucher im Sinne dieser AGB ist jede natürliche Person, die ein Rechtsgeschäft zu Zwecken 
        abschließt, die überwiegend weder ihrer gewerblichen noch ihrer selbständigen beruflichen Tätigkeit 
        zugerechnet werden können.
      </p>
      <p>
        (3) Unternehmer im Sinne dieser AGB ist eine natürliche oder juristische Person oder eine 
        rechtsfähige Personengesellschaft, die bei Abschluss eines Rechtsgeschäfts in Ausübung ihrer 
        gewerblichen oder selbständigen beruflichen Tätigkeit handelt.
      </p>
    </section>

    <section>
      <h2 className="font-heading text-2xl uppercase mt-8 mb-4">§ 2 Vertragsschluss</h2>
      <p>
        (1) Die im Online-Shop des Verkäufers enthaltenen Produktbeschreibungen stellen keine verbindlichen 
        Angebote seitens des Verkäufers dar, sondern dienen zur Abgabe eines verbindlichen Angebots durch 
        den Kunden.
      </p>
      <p>
        (2) Der Kunde kann das Angebot über das in den Online-Shop des Verkäufers integrierte 
        Online-Bestellformular abgeben. Dabei gibt der Kunde, nachdem er die ausgewählten Waren in den 
        virtuellen Warenkorb gelegt und den elektronischen Bestellprozess durchlaufen hat, durch Klicken 
        des den Bestellvorgang abschließenden Buttons ein rechtlich verbindliches Vertragsangebot in Bezug 
        auf die im Warenkorb enthaltenen Waren ab.
      </p>
      <p>
        (3) Der Verkäufer kann das Angebot des Kunden innerhalb von fünf Tagen annehmen,
      </p>
      <ul className="list-disc pl-6 mt-2">
        <li>indem er dem Kunden eine schriftliche Auftragsbestätigung oder eine Auftragsbestätigung in 
        Textform (Fax oder E-Mail) übermittelt, wobei insoweit der Zugang der Auftragsbestätigung beim 
        Kunden maßgeblich ist, oder</li>
        <li>indem er dem Kunden die bestellte Ware liefert, wobei insoweit der Zugang der Ware beim Kunden 
        maßgeblich ist, oder</li>
        <li>indem er den Kunden nach Abgabe von dessen Bestellung zur Zahlung auffordert.</li>
      </ul>
      <p className="mt-2">
        (4) Der Vertragstext wird vom Verkäufer gespeichert und dem Kunden nach Vertragsschluss nebst den 
        vorliegenden AGB per E-Mail zugesendet.
      </p>
    </section>

    <section>
      <h2 className="font-heading text-2xl uppercase mt-8 mb-4">§ 3 Preise und Zahlungsbedingungen</h2>
      <p>
        (1) Sofern sich aus der Produktbeschreibung des Verkäufers nichts anderes ergibt, handelt es sich 
        bei den angegebenen Preisen um Gesamtpreise, die die gesetzliche Umsatzsteuer enthalten. 
        Gegebenenfalls zusätzlich anfallende Liefer- und Versandkosten werden in der jeweiligen 
        Produktbeschreibung gesondert angegeben.
      </p>
      <p>
        (2) Die Zahlung erfolgt wahlweise per [Zahlungsarten einfügen, z.B. PayPal, Kreditkarte, Vorkasse].
      </p>
    </section>

    <section>
      <h2 className="font-heading text-2xl uppercase mt-8 mb-4">§ 4 Lieferbedingungen</h2>
      <p>
        (1) Die Lieferung von Waren erfolgt auf dem Versandweg an die vom Kunden angegebene Lieferanschrift, 
        sofern nichts anderes vereinbart ist.
      </p>
      <p>
        (2) Bei Waren, die im Print-on-Demand-Verfahren hergestellt werden, beginnt die Lieferfrist mit 
        Vertragsschluss. Die voraussichtliche Lieferzeit wird im Bestellprozess angegeben.
      </p>
      <p>
        (3) Scheitert die Zustellung der Ware aus Gründen, die der Kunde zu vertreten hat, trägt der Kunde 
        die dem Verkäufer hierdurch entstehenden angemessenen Kosten.
      </p>
    </section>

    <section>
      <h2 className="font-heading text-2xl uppercase mt-8 mb-4">§ 5 Eigentumsvorbehalt</h2>
      <p>
        Bis zur vollständigen Zahlung verbleibt die gelieferte Ware im Eigentum des Verkäufers.
      </p>
    </section>

    <section>
      <h2 className="font-heading text-2xl uppercase mt-8 mb-4">§ 6 Mängelhaftung (Gewährleistung)</h2>
      <p>
        (1) Ist die Kaufsache mangelhaft, gelten die Vorschriften der gesetzlichen Mängelhaftung.
      </p>
      <p>
        (2) Der Kunde wird gebeten, angelieferte Waren mit offensichtlichen Transportschäden bei dem 
        Zusteller zu reklamieren und den Verkäufer hiervon in Kenntnis zu setzen. Kommt der Kunde dem 
        nicht nach, hat dies keinerlei Auswirkungen auf seine gesetzlichen oder vertraglichen 
        Mängelansprüche.
      </p>
    </section>

    <section>
      <h2 className="font-heading text-2xl uppercase mt-8 mb-4">§ 7 Haftung</h2>
      <p>
        (1) Für eine Haftung des Verkäufers auf Schadensersatz gelten unbeschadet der sonstigen gesetzlichen 
        Anspruchsvoraussetzungen folgende Haftungsausschlüsse und -begrenzungen.
      </p>
      <p>
        (2) Der Verkäufer haftet unbeschränkt, soweit die Schadensursache auf Vorsatz oder grober 
        Fahrlässigkeit beruht.
      </p>
      <p>
        (3) Ferner haftet der Verkäufer für die leicht fahrlässige Verletzung von wesentlichen Pflichten, 
        deren Verletzung die Erreichung des Vertragszwecks gefährdet, oder für die Verletzung von Pflichten, 
        deren Erfüllung die ordnungsgemäße Durchführung des Vertrages überhaupt erst ermöglicht und auf 
        deren Einhaltung der Kunde regelmäßig vertraut.
      </p>
    </section>

    <section>
      <h2 className="font-heading text-2xl uppercase mt-8 mb-4">§ 8 Anwendbares Recht</h2>
      <p>
        (1) Für sämtliche Rechtsbeziehungen der Parteien gilt das Recht der Bundesrepublik Deutschland unter 
        Ausschluss der Gesetze über den internationalen Kauf beweglicher Waren.
      </p>
      <p>
        (2) Bei Verbrauchern gilt diese Rechtswahl nur insoweit, als nicht der gewährte Schutz durch 
        zwingende Bestimmungen des Rechts des Staates, in dem der Verbraucher seinen gewöhnlichen Aufenthalt 
        hat, entzogen wird.
      </p>
    </section>

    <section>
      <h2 className="font-heading text-2xl uppercase mt-8 mb-4">§ 9 Schlussbestimmungen</h2>
      <p>
        (1) Sollten einzelne Bestimmungen dieses Vertrages unwirksam sein oder werden, so bleibt die 
        Wirksamkeit der übrigen Bestimmungen davon unberührt.
      </p>
      <p>
        (2) Stand der AGB: [Aktuelles Datum einfügen]
      </p>
    </section>

    <div className="mt-8 p-4 bg-yellow-50 border-l-4 border-yellow-400">
      <p className="text-sm">
        <strong>Hinweis:</strong> Diese AGB sind ein Muster und sollten vor Verwendung von einem 
        Rechtsanwalt geprüft und an die individuellen Gegebenheiten Ihres Unternehmens angepasst werden.
      </p>
    </div>
  </LegalPage>
);

// ============= ADMIN =============
const Admin = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [aiRequest, setAiRequest] = useState({ vorwahl: "", stadt: "", category: "t-shirts" });
  const [generatedDescription, setGeneratedDescription] = useState("");
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("vl_admin_token");
    if (token) verifyToken(token);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (isLoggedIn) { fetchStats(); fetchOrders(); } }, [isLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  const verifyToken = async (token) => {
    try {
      const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.is_admin) setIsLoggedIn(true);
    } catch (error) { localStorage.removeItem("vl_admin_token"); }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, loginData);
      if (res.data.user.is_admin) {
        localStorage.setItem("vl_admin_token", res.data.token);
        setIsLoggedIn(true);
        toast.success("Erfolgreich angemeldet!");
      } else { toast.error("Keine Admin-Rechte"); }
    } catch (error) { toast.error("Login fehlgeschlagen"); }
    setLoading(false);
  };

  const handleLogout = () => { localStorage.removeItem("vl_admin_token"); setIsLoggedIn(false); };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("vl_admin_token");
      const res = await axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } });
      setStats(res.data);
    } catch (error) { console.error(error); }
  };

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem("vl_admin_token");
      const res = await axios.get(`${API}/admin/orders`, { headers: { Authorization: `Bearer ${token}` } });
      setOrders(res.data.orders);
    } catch (error) { console.error(error); }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      const token = localStorage.getItem("vl_admin_token");
      await axios.put(`${API}/admin/orders/${orderId}/status`, null, { params: { status }, headers: { Authorization: `Bearer ${token}` } });
      toast.success("Status aktualisiert");
      fetchOrders();
    } catch (error) { toast.error("Fehler"); }
  };

  const generateDescription = async () => {
    if (!aiRequest.vorwahl || !aiRequest.stadt) { toast.error("Bitte Vorwahl und Stadt eingeben"); return; }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/ai/generate-description`, aiRequest);
      setGeneratedDescription(res.data.description);
    } catch (error) { toast.error("Fehler bei der Generierung"); }
    setGenerating(false);
  };

  if (!isLoggedIn) {
    return (
      <main className="min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <h1 className="font-heading text-4xl uppercase mb-8 text-center">Admin Login</h1>
          <form onSubmit={handleLogin} className="space-y-4">
            <div><Label className="font-mono text-xs uppercase">Email</Label><Input type="email" value={loginData.email} onChange={(e) => setLoginData({ ...loginData, email: e.target.value })} className="sharp mt-1" required /></div>
            <div><Label className="font-mono text-xs uppercase">Passwort</Label><Input type="password" value={loginData.password} onChange={(e) => setLoginData({ ...loginData, password: e.target.value })} className="sharp mt-1" required /></div>
            <Button type="submit" className="w-full sharp btn-primary" disabled={loading}>{loading ? "..." : "Anmelden"}</Button>
          </form>
          <p className="font-mono text-xs text-gray-500 mt-4 text-center">Demo: admin@vorwahlliebe.de / admin123</p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="font-heading text-4xl uppercase">Admin Dashboard</h1>
          <Button onClick={handleLogout} variant="ghost" className="font-mono text-sm"><LogOut size={16} className="mr-2" /> Logout</Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="sharp mb-8">
            <TabsTrigger value="dashboard" className="sharp">Dashboard</TabsTrigger>
            <TabsTrigger value="orders" className="sharp">Bestellungen</TabsTrigger>
            <TabsTrigger value="ai" className="sharp">AI Generator</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-[#FAFAFA] p-6 border-l-4 border-black"><p className="font-mono text-xs uppercase text-gray-500">Bestellungen</p><p className="font-heading text-4xl">{stats?.total_orders || 0}</p></div>
              <div className="bg-[#FAFAFA] p-6 border-l-4 border-[#FF4500]"><p className="font-mono text-xs uppercase text-gray-500">Umsatz</p><p className="font-heading text-4xl">€{(stats?.total_revenue || 0).toFixed(2)}</p></div>
              <div className="bg-[#FAFAFA] p-6 border-l-4 border-green-500"><p className="font-mono text-xs uppercase text-gray-500">Produkte</p><p className="font-heading text-4xl">{stats?.total_products || 0}</p></div>
            </div>
            <h2 className="font-heading text-2xl uppercase mb-4">Letzte Bestellungen</h2>
            <div className="space-y-4">
              {stats?.recent_orders?.slice(0, 5).map(order => (
                <div key={order.id} className="bg-[#FAFAFA] p-4 flex justify-between items-center">
                  <div><p className="font-mono text-sm">{order.order_number}</p><p className="font-body text-xs text-gray-500">{order.shipping?.email}</p></div>
                  <div className="text-right"><p className="font-mono">€{order.total?.toFixed(2)}</p><Badge className={`sharp ${order.status === "paid" ? "bg-green-500" : "bg-gray-500"}`}>{order.status}</Badge></div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="orders">
            <h2 className="font-heading text-2xl uppercase mb-6">Alle Bestellungen</h2>
            <div className="space-y-4">
              {orders.map(order => (
                <div key={order.id} className="bg-[#FAFAFA] p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="font-mono text-lg">{order.order_number}</p>
                      <p className="font-body text-sm text-gray-500">{order.shipping?.first_name} {order.shipping?.last_name} - {order.shipping?.email}</p>
                      <p className="font-body text-sm text-gray-500">{order.shipping?.street}, {order.shipping?.zip_code} {order.shipping?.city}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-xl">€{order.total?.toFixed(2)}</p>
                      <Select value={order.status} onValueChange={(v) => updateOrderStatus(order.id, v)}>
                        <SelectTrigger className="w-[140px] sharp mt-2"><SelectValue /></SelectTrigger>
                        <SelectContent className="sharp">
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="paid">Paid</SelectItem>
                          <SelectItem value="processing">Processing</SelectItem>
                          <SelectItem value="shipped">Shipped</SelectItem>
                          <SelectItem value="delivered">Delivered</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Separator className="my-4" />
                  <div className="space-y-2">
                    {order.items?.map((item, idx) => <p key={idx} className="font-mono text-sm">{item.quantity}× {item.name} ({item.size}/{item.color}) - €{item.price?.toFixed(2)}</p>)}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="ai">
            <h2 className="font-heading text-2xl uppercase mb-6">AI Beschreibungs-Generator</h2>
            <div className="max-w-xl">
              <div className="space-y-4 mb-6">
                <div><Label className="font-mono text-xs uppercase">Vorwahl</Label><Input value={aiRequest.vorwahl} onChange={(e) => setAiRequest({ ...aiRequest, vorwahl: e.target.value })} placeholder="z.B. 0521" className="sharp mt-1" /></div>
                <div><Label className="font-mono text-xs uppercase">Stadt</Label><Input value={aiRequest.stadt} onChange={(e) => setAiRequest({ ...aiRequest, stadt: e.target.value })} placeholder="z.B. Bielefeld" className="sharp mt-1" /></div>
                <div>
                  <Label className="font-mono text-xs uppercase">Kategorie</Label>
                  <Select value={aiRequest.category} onValueChange={(v) => setAiRequest({ ...aiRequest, category: v })}>
                    <SelectTrigger className="sharp mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="sharp">
                      <SelectItem value="t-shirts">T-Shirt</SelectItem>
                      <SelectItem value="hoodies">Hoodie</SelectItem>
                      <SelectItem value="caps">Cap</SelectItem>
                      <SelectItem value="beanies">Beanie</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={generateDescription} className="sharp btn-primary" disabled={generating}>{generating ? "Generiere..." : "Beschreibung generieren"}</Button>
              {generatedDescription && (
                <div className="mt-6 bg-[#FAFAFA] p-6 border-l-4 border-[#FF4500]">
                  <Label className="font-mono text-xs uppercase text-gray-500 mb-2 block">Generierte Beschreibung:</Label>
                  <p className="font-body leading-relaxed">{generatedDescription}</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
};

// ============= MAIN APP =============
function App() {
  const [cart, setCart] = useState({ items: [] });

  useEffect(() => {
    fetchCart();
    seedData();
    const handleCartUpdate = () => fetchCart();
    window.addEventListener("cartUpdated", handleCartUpdate);
    return () => window.removeEventListener("cartUpdated", handleCartUpdate);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const seedData = async () => { try { await axios.post(`${API}/seed`); } catch (error) {} };

  const fetchCart = async () => {
    try {
      const res = await axios.get(`${API}/cart/${getSessionId()}`);
      setCart(res.data);
    } catch (error) { console.error(error); }
  };

  const addToCart = async (product, variant, quantity = 1) => {
    try {
      await axios.post(`${API}/cart/${getSessionId()}/add`, {
        product_id: product.id, variant_id: variant.id, quantity,
        name: product.name, vorwahl: product.vorwahl, size: variant.size, color: variant.color, price: variant.price, image_url: product.image_url
      });
      fetchCart();
    } catch (error) { toast.error("Fehler beim Hinzufügen"); }
  };

  return (
    <div className="App min-h-screen flex flex-col">
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Header cart={cart} />
        <div className="flex-1">
          <Routes>
            <Route path="/" element={<Home addToCart={addToCart} />} />
            <Route path="/shop" element={<SpreadshopWidget />} />
            <Route path="/old-shop" element={<OldShop addToCart={addToCart} />} />
            <Route path="/vorwahlen" element={<VorwahlenPage />} />
            <Route path="/product/:id" element={<ProductDetail addToCart={addToCart} />} />
            <Route path="/checkout" element={<Checkout cart={cart} />} />
            <Route path="/order-confirmation/:id" element={<OrderConfirmation />} />
            <Route path="/about" element={<About />} />
            <Route path="/impressum" element={<Impressum />} />
            <Route path="/datenschutz" element={<Datenschutz />} />
            <Route path="/widerruf" element={<Widerruf />} />
            <Route path="/agb" element={<AGB />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="*" element={<Home addToCart={addToCart} />} />
          </Routes>
        </div>
        <Footer />
      </BrowserRouter>
    </div>
  );
}

export default App;
